import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { openHtmlInNewTab, copyTextToClipboard } from '../utils/openDocument';
import { Link } from 'react-router-dom';
import { useCrm } from '../store';
import { Card, PageTitle, StatutBadge } from '../ui';
import { FileText, Plus, Trash2, Edit3, Copy, Search, X, RefreshCcw, Link2, Sparkles, Download, ClipboardCopy, Eye, ExternalLink, Pencil, ChevronDown, ChevronRight, Lock, Send, PanelRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { formatEUR, formatDateFR } from '../utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { withDemoFlag, demoFilterValue } from '../demoMode';
import { useConfirm } from '../components/ConfirmDialog';
import { logoSrc } from '@/crm/contract/branding';
import { exportHtmlToPdf } from '../utils/pdfExport';
import { resetRelanceNotificationForPrestation } from '../utils/relanceNotif';
import { adresseLignes } from '@/crm/utils/adresse';
import { EnvoiDocumentModal } from '@/crm/components/EnvoiDocumentModal';
import { round2, resteAFacturer, repartirSolde, echeancierEquilibre, prixNetHT } from '@/crm/utils/finance';

type Statut = 'Brouillon' | 'Finalisée' | 'Envoyée' | 'Acceptée' | 'Refusée' | 'Expirée' | 'Annulée';
const STATUTS: Statut[] = ['Brouillon', 'Finalisée', 'Envoyée', 'Acceptée', 'Refusée', 'Expirée', 'Annulée'];
/** « Brouillon » est affiché « Provisoire » (vocabulaire du cahier des charges). */
const statutLabel = (s: Statut) => (s === 'Brouillon' ? 'Provisoire' : s);

/** Ajout rapide d'un produit du catalogue dans les lignes de proposition. */
function CatalogueAdd({ onAdd }: { onAdd: (l: Omit<Ligne, 'ordre'>) => void }) {
  const data = useCrm();
  const catalogue = ((data.produits || []) as any[]).filter((p) => p.actif !== false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    return catalogue
      .filter((p) => (term ? `${p.reference || ''} ${p.libelle} ${p.categorie} ${p.sousFamille || ''} ${p.description}`.toLowerCase().includes(term) : true))
      .slice(0, 12);
  }, [catalogue, q]);

  if (!catalogue.length) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-1.5 rounded-lg border border-teal-500/40 bg-teal-500/10 text-teal-200 text-xs font-medium flex items-center gap-1.5 hover:bg-teal-500/20"
      >
        <Plus size={13} strokeWidth={1.5} /> Produit / location
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher dans le catalogue…"
            className="w-full mb-2 bg-slate-950/60 border border-slate-800 rounded px-2.5 py-1.5 text-xs text-slate-200 placeholder:text-slate-600"
          />
          <div className="max-h-72 overflow-auto">
            {results.length === 0 && (
              <div className="text-xs text-slate-500 italic px-2 py-2">Aucun produit trouvé.</div>
            )}
            {results.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  onAdd({
                    titre: p.libelle,
                    description: p.description || '',
                    quantite: 1,
                    prixUnitaireHT: Number(p.prixHT || 0),
                    prixBrutHT: Number(p.prixHT || 0),
                    reference: p.reference || null,
                    unite: p.unite || null,
                    artisteId: null,
                  });
                  setOpen(false);
                  setQ('');
                }}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-800 border-b border-slate-800/50 last:border-0"
              >
                <div className="text-xs text-slate-200 truncate">{p.reference && <span className="font-mono text-teal-300 mr-1">{p.reference}</span>}{p.libelle}</div>
                <div className="text-[10px] text-slate-500 truncate">
                  {p.categorie || '—'} · {formatEUR(Number(p.prixHT || 0))} HT
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type Ligne = {
  id?: string;
  ordre: number;
  titre: string;
  description: string;
  quantite: number;
  prixUnitaireHT: number;           // prix unitaire NET HT (après remise ligne)
  artisteId?: string | null;
  reference?: string | null;
  unite?: string | null;
  remisePct?: number;
  prixBrutHT?: number | null;
  typeLigne?: string;
};

type StructureCode = 'cygnature' | 'suivez_vos_reves';
const STRUCTURE_BRAND: Record<StructureCode, { name: string; tagline: string; alt: string }> = {
  cygnature: { name: 'CYGNATURE PRODUCTION', tagline: 'Agence événementielle · AUBE - CHAMPAGNE', alt: 'Cygnature Production' },
  suivez_vos_reves: { name: 'SUIVEZ VOS RÊVES EVENT', tagline: 'Agence événementielle · AUBE - CHAMPAGNE', alt: 'Suivez Vos Rêves Event' },
};
type Proposition = {
  id: string;
  numero: string | null;
  prestation_id: string | null;
  client_id: string | null;
  client_nom: string;
  date_event: string | null;
  lieu: string | null;
  type_event: string | null;
  note_intro: string | null;
  validite_jours: number;
  acompte_pct: number;
  acompte_jours: number;
  mode_paiement: string;
  nb_echeances: number;
  statut: Statut;
  version: number;
  parent_id: string | null;
  envoye_at: string | null;
  decision_at: string | null;
  devis_id: string | null;
  structure: StructureCode;
  echeancier_overrides: EcheanceOverride[] | null;
  texte_cloture: string | null;
  remarques: string | null;
  visuel_data_url: string | null;
  visuel_name: string | null;
  visuel2_data_url: string | null;
  visuel2_name: string | null;
  visuel3_data_url: string | null;
  visuel3_name: string | null;
  created_at: string;
  updated_at: string;
  lignes: Ligne[];
  remise_pct: number;
  tva_pct: number;
};

type EcheanceOverride = { label?: string; date?: string; montant?: number };
type Visual = { dataUrl: string | null; name: string | null };

const TEXTE_CLOTURE_DEFAUT = `Dans l'attente de votre retour, nous restons à votre disposition pour avancer rapidement sur votre demande et bloquer officiellement la date souhaitée, sous réserve de disponibilité au moment de votre accord.
Dès votre validation, nous vous transmettrons un devis-contrat complet à signer et à nous retourner. Ce document permettra d'officialiser notre collaboration, de confirmer la réservation de la prestation et de procéder à l'émission de la facture correspondante.

Bien cordialement,
L'équipe Cygnature Production`;

// Récupère le taux de TVA saisi dans le calculateur (calc_data.tvaPct) des lignes artistes
function tvaPctFromCalcRows(rows: any[] | null | undefined): number | null {
  for (const r of rows || []) {
    const v = Number((r?.calc_data as any)?.tvaPct);
    if (Number.isFinite(v) && v >= 0) return v;
  }
  return null;
}

const emptyProp = (modePaiement: string): Proposition => ({
  id: '',
  numero: null,
  prestation_id: null,
  client_id: null,
  client_nom: '',
  date_event: null,
  lieu: null,
  type_event: null,
  note_intro: '',
  validite_jours: 30,
  acompte_pct: 30,
  acompte_jours: 15,
  mode_paiement: modePaiement,
  nb_echeances: 1,
  statut: 'Brouillon',
  version: 1,
  parent_id: null,
  envoye_at: null,
  decision_at: null,
  devis_id: null,
  structure: 'cygnature',
  echeancier_overrides: null,
  texte_cloture: null,
  remarques: null,
  visuel_data_url: null,
  visuel_name: null,
  visuel2_data_url: null,
  visuel2_name: null,
  visuel3_data_url: null,
  visuel3_name: null,
  created_at: '',
  updated_at: '',
  lignes: [],
  remise_pct: 0,
  tva_pct: 20,
});

export function rowToProp(r: any, lignes: any[] = []): Proposition {
  return {
    id: r.id,
    numero: r.numero,
    prestation_id: r.prestation_id,
    client_id: r.client_id,
    client_nom: r.client_nom || '',
    date_event: r.date_event,
    lieu: r.lieu,
    type_event: r.type_event,
    note_intro: r.note_intro || '',
    validite_jours: r.validite_jours ?? 30,
    acompte_pct: Number(r.acompte_pct ?? 30),
    acompte_jours: Number(r.acompte_jours ?? 15),
    mode_paiement: r.mode_paiement || 'Virement bancaire',
    nb_echeances: r.nb_echeances ?? 1,
    statut: r.statut,
    version: r.version ?? 1,
    parent_id: r.parent_id,
    envoye_at: r.envoye_at,
    decision_at: r.decision_at,
    devis_id: r.devis_id,
    structure: (r.structure as StructureCode) || 'cygnature',
    echeancier_overrides: Array.isArray(r.echeancier_overrides) ? r.echeancier_overrides : null,
    texte_cloture: r.texte_cloture ?? null,
    remarques: r.conditions ?? null,
    visuel_data_url: r.visuel_data_url ?? null,
    visuel_name: r.visuel_name ?? null,
    visuel2_data_url: r.visuel2_data_url ?? null,
    visuel2_name: r.visuel2_name ?? null,
    visuel3_data_url: r.visuel3_data_url ?? null,
    visuel3_name: r.visuel3_name ?? null,
    created_at: r.created_at,
    updated_at: r.updated_at,
    lignes: lignes.map((l) => ({
      id: l.id,
      ordre: l.ordre ?? 0,
      titre: l.titre || '',
      description: l.description || '',
      quantite: Number(l.quantite ?? 1),
      prixUnitaireHT: Number(l.prix_unitaire_ht ?? 0),
      artisteId: l.artiste_id,
      reference: l.reference ?? null,
      unite: l.unite ?? null,
      remisePct: Number(l.remise_pct ?? 0),
      prixBrutHT: l.prix_brut_ht == null ? null : Number(l.prix_brut_ht),
      typeLigne: l.type_ligne || 'article',
    })),
    remise_pct: Number(r.remise_pct ?? 0),
    tva_pct: r.tva_pct === null || r.tva_pct === undefined ? 20 : Number(r.tva_pct),
  };
}

const normalizeDomain = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const uniqueDomains = (values: Array<string | null | undefined>) => Array.from(new Map(
  values.filter(Boolean).map((v) => [normalizeDomain(v!), v!.trim()])
).values());

function lineTitleFromDomains(artist: any, prestationDomains: string[] = []) {
  const selected = uniqueDomains(prestationDomains);
  const artistDomains = uniqueDomains([artist?.domaine_principal, ...((artist?.domaines || []) as string[])]);
  const artistKeys = new Set(artistDomains.map(normalizeDomain));
  const matched = selected.filter((d) => artistKeys.has(normalizeDomain(d)));
  const domains = matched.length ? matched : (selected.length ? selected : artistDomains);
  return domains.length ? domains.join(' / ') : 'Prestation artistique';
}

function prestationDescription(row: any) {
  return [row?.prop_note, row?.remarques].map((v) => String(v || '').trim()).filter(Boolean).join('\n\n');
}

const PROP_STATUT_COLORS: Record<Statut, string> = {
  'Brouillon': 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  'Envoyée': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'Acceptée': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Refusée': 'bg-red-500/15 text-red-300 border-red-500/30',
  'Expirée': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  'Finalisée': 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  'Annulée': 'bg-slate-700/30 text-slate-400 border-slate-600/40',
};

function PropStatutInlineSelect({ p, onChanged }: { p: Proposition; onChanged: (next: Statut) => void }) {
  const [val, setVal] = useState<Statut>(p.statut);
  useEffect(() => { setVal(p.statut); }, [p.statut]);
  const cls = PROP_STATUT_COLORS[val];
  return (
    <select
      value={val}
      onChange={async (e) => {
        const next = e.target.value as Statut;
        const prev = val;
        setVal(next);
        try {
          const patch: any = { statut: next };
          if (next === 'Envoyée') patch.envoye_at = new Date().toISOString();
          if (next === 'Acceptée' || next === 'Refusée') patch.decision_at = new Date().toISOString();
          const { error } = await supabase.from('propositions').update(patch).eq('id', p.id);
          if (error) throw error;
          // Reset compteur d'inactivité : la modif de statut compte comme une action
          await resetRelanceNotificationForPrestation((p as any).prestation_id);
          toast.success(`Statut → ${next}`);
          onChanged(next);
        } catch (err: any) {
          setVal(prev);
          toast.error(err?.message || 'Erreur mise à jour statut');
        }
      }}
      onClick={(e) => e.stopPropagation()}
      className={`text-xs px-2 py-0.5 rounded-md border ${cls} bg-slate-900/40 cursor-pointer focus:outline-none focus:ring-1 focus:ring-amber-500/40`}
    >
      {STATUTS.map((s) => <option key={s} value={s} className="bg-slate-900 text-slate-100">{statutLabel(s)}</option>)}
    </select>
  );
}

export default function Propositions() {
  const data = useCrm();
  const confirm = useConfirm();
  const [list, setList] = useState<Proposition[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Proposition | null>(null);
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState<Statut | 'all'>('all');

  const loadList = useCallback(async () => {
    setLoading(true);
    const { data: props, error } = await supabase
      .from('propositions')
      .select('*')
      .eq('is_demo', demoFilterValue())
      .order('created_at', { ascending: false });
    if (error) { toast.error('Erreur chargement propositions'); setLoading(false); return; }
    const ids = (props || []).map((p) => p.id);
    let lignesByProp = new Map<string, any[]>();
    if (ids.length) {
      const { data: lignes } = await supabase
        .from('propositions_lignes')
        .select('*')
        .in('proposition_id', ids)
        .order('ordre');
      (lignes || []).forEach((l: any) => {
        const arr = lignesByProp.get(l.proposition_id) || [];
        arr.push(l);
        lignesByProp.set(l.proposition_id, arr);
      });
    }
    setList((props || []).map((p: any) => rowToProp(p, lignesByProp.get(p.id) || [])));
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const filtered = useMemo(() => list
    .filter((p) => statutFilter === 'all' || p.statut === statutFilter)
    .filter((p) => !search ||
      p.client_nom.toLowerCase().includes(search.toLowerCase()) ||
      (p.numero || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.lieu || '').toLowerCase().includes(search.toLowerCase()))
  , [list, search, statutFilter]);

  // Regroupement par dossier (prestation_id ou client+date)
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; label: string; sublabel: string; sortDate: string; items: Proposition[] }>();
    for (const p of filtered) {
      // Regroupement strict par Client + Date + Lieu (versions empilées)
      const key = `${(p.client_nom || '').trim().toLowerCase()}|${p.date_event || ''}|${(p.lieu || '').trim().toLowerCase()}`;
      const g = map.get(key) || {
        key,
        label: p.client_nom || '—',
        sublabel: [p.type_event, p.lieu].filter(Boolean).join(' · ') || '—',
        sortDate: p.date_event || p.created_at,
        items: [],
      };
      g.items.push(p);
      if ((p.date_event || p.created_at) > g.sortDate) g.sortDate = p.date_event || p.created_at;
      map.set(key, g);
    }
    const arr = Array.from(map.values());
    arr.forEach((g) => g.items.sort((a, b) => (b.version || 0) - (a.version || 0) || (b.created_at || '').localeCompare(a.created_at || '')));
    arr.sort((a, b) => (b.sortDate || '').localeCompare(a.sortDate || ''));
    return arr;
  }, [filtered]);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (k: string) => setExpandedGroups((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });


  const handleNewBlank = () => {
    setEditing(emptyProp(data.paiementModes[0] ?? 'Virement bancaire'));
  };

  const handleNewFromPrestation = async (prestId: string) => {
    const p = data.prest.find((x) => x.id === prestId);
    if (!p) return;
    // Cherche la prestation_id réelle (UUID DB)
    const { data: prestRow } = await supabase
      .from('prestations')
      .select('id, client_id, domaines, prop_note, remarques')
      .eq('numero', p.id)
      .maybeSingle();
    let lignesFromCalc: Ligne[] = [];
    let tvaFromCalc: number | null = null;
    if (prestRow?.id) {
      const { data: rows } = await supabase
        .from('prestations_artistes')
        .select('id, artiste_id, artiste_nom, cout, prop_ttc, marge_pct, notes, calc_data, artistes(domaine_principal, domaines)')
        .eq('prestation_id', prestRow.id)
        .eq('inclus', true);
      tvaFromCalc = tvaPctFromCalcRows(rows);
      lignesFromCalc = (rows || []).map((r: any, i: number) => {
        // NB: la colonne `prop_ttc` de prestations_artistes stocke en réalité le VENDU HT
        // (cf. NouvelleDemande.tsx où l'on écrit `prop_ttc: p.venduHT`). Nom historique trompeur.
        const venduHT = Number(r.prop_ttc ?? 0);
        const fallbackHT = Number(r.cout ?? 0) * (1 + (Number(r.marge_pct) || 0) / 100);
        const prixHT = venduHT > 0 ? venduHT : fallbackHT;
        return {
          ordre: i,
          titre: lineTitleFromDomains(r.artistes, prestRow.domaines || p.domaines || []),
          description: prestationDescription(prestRow),
          quantite: 1,
          prixUnitaireHT: Math.round(prixHT * 100) / 100,
          artisteId: r.artiste_id || null,
        };
      });
    }
    const draft = emptyProp(data.paiementModes[0] ?? 'Virement bancaire');
    draft.prestation_id = prestRow?.id || null;
    draft.client_id = prestRow?.client_id || null;
    draft.client_nom = p.client;
    draft.date_event = p.dateEvent || null;
    draft.lieu = p.lieu || null;
    draft.type_event = (p.themes || []).join(', ') || p.libelle || null;
    draft.note_intro = `Bonjour ${p.client.split(' ')[0] || ''},\nVeuillez trouver ci-dessous notre proposition pour votre événement.`;
    draft.lignes = lignesFromCalc;
    if (tvaFromCalc !== null) draft.tva_pct = tvaFromCalc;
    setEditing(draft);
  };

  const handleEdit = (p: Proposition) => setEditing({ ...p, lignes: [...p.lignes] });

  const handleDelete = async (p: Proposition) => {
    const ok = await confirm({ title: `Supprimer la proposition ${p.numero || ''} ?`, destructive: true, confirmLabel: 'Supprimer' });
    if (!ok) return;
    const { error } = await supabase.from('propositions').delete().eq('id', p.id);
    if (error) { toast.error('Suppression refusée'); return; }
    toast.success('Proposition supprimée');
    loadList();
  };

  const handleDuplicate = async (p: Proposition) => {
    const draft = { ...p, id: '', numero: null, statut: 'Brouillon' as Statut, version: 1, parent_id: null, envoye_at: null, decision_at: null, devis_id: null };
    setEditing(draft);
  };

  const handleNewVersion = (p: Proposition) => {
    setEditing({ ...p, id: '', numero: null, statut: 'Brouillon' as Statut, version: p.version + 1, parent_id: p.id, envoye_at: null, decision_at: null, devis_id: null });
  };

  const handleDownloadPdf = async (p: Proposition) => {
    try {
      let clientRecord: any = null;
      if (p.client_id) {
        const local = data.clients.find((c) => c.id === p.client_id);
        if (local) clientRecord = local;
        else {
          const { data: c } = await supabase.from('clients').select('*').eq('id', p.client_id).maybeSingle();
          clientRecord = c || null;
        }
      }
      const lignes = p.lignes;
      const totalHT = lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT, 0);
      const remisePct = Number(p.remise_pct) || 0;
      const remiseMontant = remisePct > 0 ? Math.round(totalHT * (remisePct / 100) * 100) / 100 : 0;
      const netHT = totalHT - remiseMontant;
      const tvaPct = p.tva_pct === null || p.tva_pct === undefined ? 20 : Number(p.tva_pct);
      const totalTVA = Math.round(netHT * (tvaPct / 100) * 100) / 100;
      const totalTTC = Math.round((netHT + totalTVA) * 100) / 100;

      const nb = Math.max(1, Math.min(24, p.nb_echeances || 1));
      const acomptePct = Math.max(0, Math.min(100, p.acompte_pct || 0));
      const acompte = Math.round(totalTTC * acomptePct) / 100;
      const solde = Math.max(0, totalTTC - acompte);
      const refDate = p.date_event ? new Date(p.date_event) : new Date();
      const rows: { label: string; date: string; montant: number }[] = [];
      if (acompte > 0) {
        const aDate = new Date();
        aDate.setDate(aDate.getDate() + Math.max(0, p.acompte_jours || 0));
        rows.push({ label: `Acompte (${acomptePct}%)`, date: aDate.toISOString().slice(0, 10), montant: acompte });
      }
      let cumul = 0;
      const base = solde / nb;
      for (let i = 1; i <= nb; i++) {
        const d = new Date(refDate);
        d.setMonth(d.getMonth() + (i - nb));
        const isLast = i === nb;
        const montant = isLast ? Math.round((solde - cumul) * 100) / 100 : Math.round(base * 100) / 100;
        cumul += montant;
        const label = isLast ? 'Solde' : (nb > 1 ? `Traite ${i}/${nb - 1}` : 'Solde');
        rows.push({ label, date: d.toISOString().slice(0, 10), montant });
      }
      const overrides = p.echeancier_overrides || [];
      const merged = rows.map((r, i) => {
        const o = overrides[i] || {};
        return {
          label: o.label ?? r.label,
          date: o.date ?? r.date,
          montant: typeof o.montant === 'number' && !Number.isNaN(o.montant) ? o.montant : r.montant,
        };
      });
      const finalAcompte = merged.length && acompte > 0 ? merged[0].montant : 0;
      const finalSolde = merged.slice(acompte > 0 ? 1 : 0).reduce((s, r) => s + (r.montant || 0), 0);
      const echeancier = { acompte: finalAcompte, solde: finalSolde, rows: merged };

      const slug = (p.client_nom || 'client').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const date = p.date_event ? p.date_event.replace(/-/g, '') : new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `Proposition-Cygnature-${slug}-${date}.pdf`;
      const html = renderPropositionHTML(p, totalHT, echeancier, fileName, {
        tvaPct, remise_pct: p.remise_pct,
        remarques: p.remarques || '',
        visuels: [
          { dataUrl: p.visuel_data_url, name: p.visuel_name },
          { dataUrl: p.visuel2_data_url, name: p.visuel2_name },
          { dataUrl: p.visuel3_data_url, name: p.visuel3_name },
        ],
        client: clientRecord,
      });
      await exportHtmlToPdf(html, { fileName, rootId: '__prop_pdf_list_root' });
    } catch (e: any) {
      console.error(e);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  if (editing) {
    return (
      <Editeur
        proposition={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); loadList(); }}
      />
    );
  }

  return (
    <div>
      <PageTitle
        title="Propositions"
        subtitle="Devis préparatoires en HT — convertibles en devis officiel"
        icon={<FileText className="text-amber-400" size={24} strokeWidth={1.5} />}
        action={
          <div className="flex gap-2">
            <button onClick={handleNewBlank} className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded-lg text-sm text-amber-300 flex items-center gap-2">
              <Plus size={14} strokeWidth={1.5} /> Nouvelle proposition
            </button>
          </div>
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher client, numéro, lieu..."
              className="w-full pl-9 pr-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value as any)}
            className="px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200"
          >
            <option value="all">Tous statuts</option>
            {STATUTS.map((s) => <option key={s} value={s}>{statutLabel(s)}</option>)}
          </select>
          <select
            onChange={(e) => { if (e.target.value) { handleNewFromPrestation(e.target.value); e.target.value = ''; } }}
            className="px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200"
            defaultValue=""
          >
            <option value="">+ Depuis une prestation…</option>
            {data.prest.filter((p) => !p.archivedAt).map((p) => (
              <option key={p.id} value={p.id}>{p.id} — {p.client}{p.dateEvent ? ` (${formatDateFR(p.dateEvent)})` : ''}</option>
            ))}
          </select>
          <button onClick={loadList} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-slate-300 flex items-center gap-1.5">
            <RefreshCcw size={13} strokeWidth={1.5} /> Recharger
          </button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Aucune proposition. <button onClick={handleNewBlank} className="text-amber-400 hover:underline">Créer la première</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-slate-500 uppercase tracking-widest border-b border-slate-800 bg-slate-950/40">
                <th className="text-left py-3 px-4 font-normal">Statut</th>
                <th className="text-left py-3 px-4 font-normal">Client / Événement</th>
                <th className="text-left py-3 px-4 font-normal">Créée le</th>
                <th className="text-left py-3 px-4 font-normal">Date event</th>
                <th className="text-right py-3 px-4 font-normal">Total HT</th>
                <th className="text-left py-3 px-4 font-normal">Version</th>
                <th className="text-right py-3 px-4 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const latest = g.items[0];
                const isExpanded = expandedGroups.has(g.key) || g.items.length === 1;
                const renderRow = (p: Proposition, indent: boolean) => {
                  const totalHT = p.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT, 0);
                  return (
                    <tr key={p.id} className={`border-b border-slate-800/50 last:border-0 hover:bg-slate-800/20 ${indent ? 'bg-slate-950/30' : ''}`}>
                      <td className="py-3 px-4">
                        <PropStatutInlineSelect p={p} onChanged={(next) => setList((arr) => arr.map((x) => x.id === p.id ? { ...x, statut: next } : x))} />
                      </td>
                      <td className={`py-3 px-4 ${indent ? 'pl-12' : ''}`}>
                        {indent ? (
                          <div className="text-xs text-slate-500">{p.numero || `V${p.version}`}</div>
                        ) : (
                          <>
                            <div className="text-slate-200">{p.client_nom || '—'}</div>
                            <div className="text-xs text-slate-500">{p.type_event || '—'} · {p.lieu || '—'}</div>
                          </>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">
                        {p.created_at ? (
                          <>
                            {formatDateFR(p.created_at, { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            <span className="text-slate-500"> · {new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </>
                        ) : '—'}
                      </td>
                      <td className="py-3 px-4 text-slate-400">{p.date_event ? formatDateFR(p.date_event) : '—'}</td>
                      <td className="py-3 px-4 text-right text-amber-400 font-medium tabular-nums">{formatEUR(totalHT)}</td>
                      <td className="py-3 px-4 text-slate-400 text-xs">V{p.version}{p.devis_id ? ' · → Devis' : ''}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => handleEdit(p)} title="Éditer" className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><Edit3 size={14} strokeWidth={1.5} /></button>
                          <button onClick={() => handleDownloadPdf(p)} title="Télécharger la proposition (PDF)" className="p-1.5 rounded hover:bg-amber-500/20 text-amber-300"><Download size={14} strokeWidth={1.5} /></button>
                          <button onClick={() => handleNewVersion(p)} title="Nouvelle version" className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><RefreshCcw size={14} strokeWidth={1.5} /></button>
                          <button onClick={() => handleDuplicate(p)} title="Dupliquer" className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><Copy size={14} strokeWidth={1.5} /></button>
                          <button onClick={() => handleDelete(p)} title="Supprimer" className="p-1.5 rounded hover:bg-red-500/20 text-red-300"><Trash2 size={14} strokeWidth={1.5} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                };

                if (g.items.length === 1) return renderRow(latest, false);

                const totalHTLatest = latest.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT, 0);
                return (
                  <React.Fragment key={`g-${g.key}`}>
                    <tr className="border-b border-slate-800/60 bg-slate-900/40 hover:bg-slate-900/60 cursor-pointer" onClick={() => toggleGroup(g.key)}>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-medium">
                          {isExpanded ? <ChevronDown size={12} strokeWidth={1.5} /> : <ChevronRight size={12} strokeWidth={1.5} />}
                          {g.items.length} versions
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-slate-100 font-medium">{g.label}</div>
                        <div className="text-xs text-slate-500">{g.sublabel}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                        Dernière : {latest.created_at ? formatDateFR(latest.created_at, { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                      </td>
                      <td className="py-3 px-4 text-slate-300 text-xs">{latest.date_event ? formatDateFR(latest.date_event) : '—'}</td>
                      <td className="py-3 px-4 text-right text-amber-400 font-medium tabular-nums">{formatEUR(totalHTLatest)}</td>
                      <td className="py-3 px-4 text-slate-400 text-xs">Dernière : V{latest.version}</td>
                      <td className="py-3 px-4 text-right text-slate-500 text-xs">
                        {isExpanded ? 'Replier' : 'Déplier'}
                      </td>
                    </tr>
                    {isExpanded && g.items.map((p) => renderRow(p, true))}
                  </React.Fragment>
                );
              })}
            </tbody>

          </table>
        )}
      </Card>
    </div>
  );
}

// =================== ÉDITEUR ===================

function Editeur({ proposition, onCancel, onSaved }: { proposition: Proposition; onCancel: () => void; onSaved: () => void }) {
  const data = useCrm();
  const [draft, setDraft] = useState<Proposition>(proposition);
  const [saving, setSaving] = useState(false);
  // Détection de modifications réelles : drapeau armé par toute interaction utilisateur
  // (pas par les chargements asynchrones initiaux). Permet d'éviter les V2 vides
  // et de déclencher l'auto-V2 dès le moindre changement.
  const dirtyRef = useRef(false);
  const markDirty = () => { dirtyRef.current = true; };
  const initialStatut = useRef<Statut>(proposition.statut);
  const [prestOptions, setPrestOptions] = useState<Array<{ id: string; numero: string | null; client_nom: string; date_event: string | null; lieu: string | null; libelle: string | null; client_id: string | null; domaines?: string[] | null; themes?: string[] | null; prop_note?: string | null; remarques?: string | null }>>([]);
  const [tvaPct, setTvaPct] = useState<number>(proposition.tva_pct ?? 20);
  const [remarques, setRemarques] = useState<string>(proposition.remarques ?? '');
    const [visuels, setVisuels] = useState<Visual[]>([
      { dataUrl: proposition.visuel_data_url ?? null, name: proposition.visuel_name ?? null },
      { dataUrl: proposition.visuel2_data_url ?? null, name: proposition.visuel2_name ?? null },
      { dataUrl: proposition.visuel3_data_url ?? null, name: proposition.visuel3_name ?? null },
    ]);
  // Indique si l'utilisateur (ou la proposition chargée) a déjà des remarques propres
  // — empêche l'écrasement automatique depuis la prestation liée.
  const remarquesTouched = useRef<boolean>(Boolean(proposition.remarques));
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [mailEditorOpen, setMailEditorOpen] = useState(false);
  const [mailSubjectOverride, setMailSubjectOverride] = useState<string | null>(null);
  const [mailBodyOverride, setMailBodyOverride] = useState<string | null>(null);
  const [calcByArtist, setCalcByArtist] = useState<Record<string, { calc: any; km: number | null; cout: number | null; prop_ttc: number | null; marge_pct: number | null }>>({});
  // Lignes désactivées (cases décochées) — permet de faire plusieurs propositions
  // avec des sous-ensembles d'artistes sans toucher au calculateur de la prestation.
  const lineKey = (l: Ligne, i: number) => l.artisteId || `idx-${i}`;
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());
  const toggleLine = (key: string) => {
    markDirty();
    setExcludedKeys((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const activeLignes = useMemo(
    () => draft.lignes.filter((l, i) => !excludedKeys.has(lineKey(l, i))),
    [draft.lignes, excludedKeys]
  );

  // Charge le détail calculateur (calc_data) pour afficher la décomposition par ligne
  useEffect(() => {
    if (!draft.prestation_id) { setCalcByArtist({}); return; }
    (async () => {
      const { data: rows } = await supabase
        .from('prestations_artistes')
        .select('artiste_id, km, cout, prop_ttc, marge_pct, calc_data')
        .eq('prestation_id', draft.prestation_id!);
      const map: Record<string, any> = {};
      (rows || []).forEach((r: any) => {
        if (r.artiste_id) map[r.artiste_id] = { calc: r.calc_data, km: r.km, cout: r.cout, prop_ttc: r.prop_ttc, marge_pct: r.marge_pct };
      });
      setCalcByArtist(map);
    })();
  }, [draft.prestation_id, draft.lignes.length]);

  // Charge les remarques de la prestation liée — uniquement si l'utilisateur n'a
  // pas déjà saisi/chargé des remarques propres à cette proposition.
  useEffect(() => {
    if (!draft.prestation_id) { return; }
    if (remarquesTouched.current) { return; }
    (async () => {
      const { data: row } = await supabase
        .from('prestations')
        .select('prop_note, remarques')
        .eq('id', draft.prestation_id!)
        .maybeSingle();
      if (row) setRemarques(prestationDescription(row));
    })();
  }, [draft.prestation_id]);

  // Charge la liste des prestations directement depuis la base (plus fiable que le store)
  useEffect(() => {
    (async () => {
      const { data: rows } = await supabase
        .from('prestations')
        .select('id, numero, client_nom, date_event, lieu, libelle, client_id, domaines, themes, prop_note, remarques')
        .is('deleted_at', null)
        .is('archived_at', null)
        .eq('is_demo', demoFilterValue())
        .order('date_event', { ascending: false })
        .limit(500);
      setPrestOptions(rows || []);
    })();
  }, []);

  // Récupère les produits/locations rattachés à un dossier sous forme de lignes de proposition
  const fetchProduitLignes = async (prestId: string, startOrdre: number): Promise<Ligne[]> => {
    try {
      const { data: rows } = await (supabase.from('prestations_produits' as any) as any)
        .select('*')
        .eq('prestation_id', prestId)
        .order('ordre');
      return ((rows || []) as any[]).map((r: any, i: number) => ({
        ordre: startOrdre + i,
        titre: r.libelle || 'Produit',
        description: r.description || '',
        quantite: Number(r.quantite ?? 1),
        prixUnitaireHT: Math.round(Number(r.prix_ht || 0) * 100) / 100,
        artisteId: null,
      }));
    } catch {
      return [];
    }
  };

  const handleLinkPrestation = async (prestId: string) => {
    markDirty();
    if (!prestId) {
      // Délier : vide tous les champs événement et les lignes
      setDraft((d) => ({
        ...d,
        prestation_id: null,
        client_id: null,
        client_nom: '',
        date_event: null,
        lieu: null,
        type_event: null,
        lignes: [],
      }));
      return;
    }
    const p = prestOptions.find((x) => x.id === prestId);
    if (!p) return;
    // Recharger automatiquement les lignes depuis le calculateur
    let lignesFromCalc: Ligne[] = [];
    try {
      const { data: rows } = await supabase
        .from('prestations_artistes')
        .select('id, artiste_id, artiste_nom, cout, prop_ttc, marge_pct, notes, calc_data, artistes(domaine_principal, domaines)')
        .eq('prestation_id', p.id)
        .eq('inclus', true);
      if (rows?.length) {
        const t = tvaPctFromCalcRows(rows);
        if (t !== null) setTvaPct(t);
        lignesFromCalc = rows.map((r: any, i: number) => {
          // `prop_ttc` stocke en réalité le vendu HT (nom historique trompeur).
          const venduHT = Number(r.prop_ttc ?? 0);
          const fallbackHT = Number(r.cout ?? 0) * (1 + (Number(r.marge_pct) || 0) / 100);
          const prixHT = venduHT > 0 ? venduHT : fallbackHT;
          const dom = lineTitleFromDomains(r.artistes, p.domaines || []);
          const titre = r.artiste_nom ? `${r.artiste_nom}${dom ? ` — ${dom}` : ''}` : dom;
          return {
            ordre: i,
            titre,
            description: r.notes || prestationDescription(p),
            quantite: 1,
            prixUnitaireHT: Math.round(prixHT * 100) / 100,
            artisteId: r.artiste_id || null,
          };
        });
      }
    } catch (e) {
      // silencieux : on garde les lignes vides
    }
    // Produits / décorations / locations rattachés au dossier
    const lignesProduits = await fetchProduitLignes(p.id, lignesFromCalc.length);
    lignesFromCalc = [...lignesFromCalc, ...lignesProduits];
    // Récupère les remarques de la prestation
    setRemarques(prestationDescription(p));
    setDraft((d) => ({
      ...d,
      prestation_id: p.id,
      client_id: p.client_id,
      client_nom: p.client_nom || '',
      date_event: p.date_event,
      lieu: p.lieu,
      type_event: (p.themes || []).join(', ') || p.libelle,
      note_intro: `Bonjour ${(p.client_nom || '').split(' ')[0] || ''},\nVeuillez trouver ci-dessous notre proposition pour votre événement.`,
      lignes: lignesFromCalc,
    }));
    if (lignesFromCalc.length > 0) {
      toast.success(`${lignesFromCalc.length} ligne(s) importée(s) du calculateur`);
    }
  };

  // Si lié à une prestation, on synchronise les infos depuis la prestation (lecture seule)
  const linkedPrest = useMemo(() => {
    if (!draft.prestation_id) return null;
    // chercher dans store par UUID DB → on a seulement le numero, donc on regarde via client_nom + date
    return data.prest.find((p) => p.client === draft.client_nom && p.dateEvent === draft.date_event) || null;
  }, [draft.prestation_id, draft.client_nom, draft.date_event, data.prest]);

  const totalHT = useMemo(
    () => activeLignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT, 0),
    [activeLignes]
  );
  const remisePct = draft.remise_pct;
  const remiseMontant = useMemo(() => Math.round(totalHT * (remisePct / 100) * 100) / 100, [totalHT, remisePct]);
  const netHT = useMemo(() => totalHT - remiseMontant, [totalHT, remiseMontant]);
  const totalTVA = useMemo(() => Math.round(netHT * (tvaPct / 100) * 100) / 100, [netHT, tvaPct]);
  const totalTTC = useMemo(() => Math.round((netHT + totalTVA) * 100) / 100, [netHT, totalTVA]);

  const echeancier = useMemo(() => {
    const nb = Math.max(1, Math.min(24, draft.nb_echeances || 1));
    const acomptePct = Math.max(0, Math.min(100, draft.acompte_pct || 0));
    const acompte = Math.round(totalTTC * acomptePct) / 100;
    const solde = Math.max(0, totalTTC - acompte);
    const refDate = draft.date_event ? new Date(draft.date_event) : new Date();
    const rows: { label: string; date: string; montant: number }[] = [];
    if (acompte > 0) {
      const aDate = new Date();
      aDate.setDate(aDate.getDate() + Math.max(0, draft.acompte_jours || 0));
      rows.push({ label: `Acompte (${acomptePct}%)`, date: aDate.toISOString().slice(0, 10), montant: acompte });
    }
    let cumul = 0;
    const base = solde / nb;
    for (let i = 1; i <= nb; i++) {
      const d = new Date(refDate);
      d.setMonth(d.getMonth() + (i - nb));
      const isLast = i === nb;
      const montant = isLast ? Math.round((solde - cumul) * 100) / 100 : Math.round(base * 100) / 100;
      cumul += montant;
      const label = isLast ? 'Solde' : (nb > 1 ? `Traite ${i}/${nb - 1}` : 'Solde');
      rows.push({ label, date: d.toISOString().slice(0, 10), montant });
    }
    // Application des overrides manuels (par index)
    const overrides = draft.echeancier_overrides || [];
    const merged = rows.map((r, i) => {
      const o = overrides[i] || {};
      return {
        label: o.label ?? r.label,
        date: o.date ?? r.date,
        montant: typeof o.montant === 'number' && !Number.isNaN(o.montant) ? o.montant : r.montant,
      };
    });
    const finalAcompte = merged.length && acompte > 0 ? merged[0].montant : 0;
    const finalSolde = merged.slice(acompte > 0 ? 1 : 0).reduce((s, r) => s + (r.montant || 0), 0);
    return { acompte: finalAcompte, solde: finalSolde, rows: merged };
  }, [totalHT, draft.acompte_pct, draft.acompte_jours, draft.nb_echeances, draft.date_event, draft.echeancier_overrides]);

  const updateEcheance = (idx: number, patch: EcheanceOverride) => {
    markDirty();
    setDraft((d) => {
      const arr = Array.isArray(d.echeancier_overrides) ? [...d.echeancier_overrides] : [];
      arr[idx] = { ...(arr[idx] || {}), ...patch };
      return { ...d, echeancier_overrides: arr };
    });
  };

  const resetEcheancier = () => {
    markDirty();
    setDraft((d) => ({ ...d, echeancier_overrides: null }));
  };



  const update = <K extends keyof Proposition>(k: K, v: Proposition[K]) => {
    if (k !== 'statut') markDirty(); // un changement de statut seul ne crée pas de nouvelle version
    setDraft((d) => {
      const next: Proposition = { ...d, [k]: v };
      // Reset des overrides quand la base de calcul change (acompte, nb échéances)
      if (k === 'acompte_pct' || k === 'acompte_jours' || k === 'nb_echeances') {
        next.echeancier_overrides = null;
      }
      return next;
    });
  };

  const addLine = () => { markDirty(); setDraft((d) => ({
    ...d,
    lignes: [...d.lignes, { ordre: d.lignes.length, titre: '', description: '', quantite: 1, prixUnitaireHT: 0 }],
  })); };

  const updateLine = (idx: number, patch: Partial<Ligne>) => { markDirty(); setDraft((d) => ({
    ...d,
    lignes: d.lignes.map((l, i) => i === idx ? { ...l, ...patch } : l),
  })); };

  const removeLine = (idx: number) => { markDirty(); setDraft((d) => ({
    ...d,
    lignes: d.lignes.filter((_, i) => i !== idx),
  })); };

  // Import depuis prestations_artistes (calculateur)
  const importFromCalculateur = async () => {
    markDirty();
    if (!draft.prestation_id) {
      toast.error('Liez d\'abord cette proposition à une prestation');
      return;
    }
    const { data: rows, error } = await supabase
      .from('prestations_artistes')
      .select('id, artiste_id, artiste_nom, cout, prop_ttc, marge_pct, artistes(domaine_principal, domaines)')
      .eq('prestation_id', draft.prestation_id)
      .eq('inclus', true);
    const lignesProduits = await fetchProduitLignes(draft.prestation_id, draft.lignes.length + (rows?.length || 0));
    if ((error || !rows?.length) && !lignesProduits.length) {
      toast.error('Aucun artiste ni produit trouvé pour cette prestation');
      return;
    }
    setDraft((d) => ({
      ...d,
      lignes: [
        ...d.lignes,
        ...(rows || []).map((r: any, i: number) => {
          // `prop_ttc` stocke en réalité le vendu HT (nom historique trompeur).
          const venduHT = Number(r.prop_ttc ?? 0);
          const fallbackHT = Number(r.cout ?? 0) * (1 + (Number(r.marge_pct) || 0) / 100);
          const prixHT = venduHT > 0 ? venduHT : fallbackHT;
          return {
            ordre: d.lignes.length + i,
            titre: lineTitleFromDomains(r.artistes, prestOptions.find((p) => p.id === draft.prestation_id)?.domaines || []),
            description: remarques.trim(),
            quantite: 1,
            prixUnitaireHT: Math.round(prixHT * 100) / 100,
            artisteId: r.artiste_id || null,
          };
        }),
        ...lignesProduits,
      ],
    }));
    toast.success(`${(rows?.length || 0) + lignesProduits.length} ligne(s) importée(s) depuis le calculateur`);
  };

  const handleSave = async () => {
    if (!draft.client_nom.trim()) { toast.error('Le nom du client est requis'); return; }

    // Détecte si quelque chose a réellement changé depuis l'ouverture de l'éditeur
    const hasChanges = dirtyRef.current;
    if (totalTTC > 0 && echeancier.rows.length && !echeancierEquilibre(echeancier.rows.map((r) => r.montant || 0), totalTTC)) {
      toast.error(`Échéancier incorrect : le total des échéances ne correspond pas au total TTC (${formatEUR(totalTTC)}). Cliquez sur « Répartir le solde ».`, { duration: 9000 });
      return;
    }

    // Cas 1 : tentative de création d'une nouvelle version (parent_id défini, pas d'id)
    // mais aucune modification effective → on annule pour ne pas polluer l'historique
    if (!draft.id && draft.parent_id && !hasChanges) {
      toast.info("Aucune modification — la nouvelle version n'a pas été créée");
      onSaved();
      return;
    }

    // Cas 2 : modification d'une proposition déjà figée (Envoyée/Acceptée/Refusée)
    // avec de vrais changements → bascule auto en nouvelle version (historique préservé)
    let workingDraft = draft;
    const figees: Statut[] = ['Finalisée', 'Envoyée', 'Acceptée', 'Refusée', 'Annulée'];
    if (draft.id && hasChanges && figees.includes(initialStatut.current)) {
      workingDraft = {
        ...draft,
        id: '',
        numero: null,
        statut: 'Brouillon',
        version: draft.version + 1,
        parent_id: draft.id,
        envoye_at: null,
        decision_at: null,
        devis_id: null,
      };
      toast.info(`Création automatique de la V${workingDraft.version} (l'originale ${initialStatut.current.toLowerCase()} est conservée)`);
    }

    setSaving(true);
    try {
      // Auto-numéro à la création (prefix_devis + prochain_numero_devis sur l'émetteur)
      let nextNumero: string | null = workingDraft.numero;
      if (!workingDraft.id && !nextNumero) {
        const structCode = workingDraft.structure || 'cygnature';
        let { data: em } = await supabase
          .from('emetteur')
          .select('id, prefix_devis, prochain_numero_devis')
          .eq('code', structCode)
          .maybeSingle();
        if (!em) {
          const r = await supabase
            .from('emetteur')
            .select('id, prefix_devis, prochain_numero_devis')
            .limit(1)
            .maybeSingle();
          em = r.data as any;
        }
        if (em) {
          const prefix = (em as any).prefix_devis || 'P-2026-';
          const next = Number((em as any).prochain_numero_devis || 1);
          nextNumero = `${prefix}${String(next).padStart(4, '0')}`;
          await supabase.from('emetteur').update({ prochain_numero_devis: next + 1 }).eq('id', (em as any).id);
        }
      }

      const payload = withDemoFlag({
        numero: nextNumero,
        prestation_id: workingDraft.prestation_id,
        client_id: workingDraft.client_id,
        client_nom: workingDraft.client_nom,
        date_event: workingDraft.date_event,
        lieu: workingDraft.lieu,
        type_event: workingDraft.type_event,
        note_intro: workingDraft.note_intro,
        validite_jours: workingDraft.validite_jours,
        acompte_pct: workingDraft.acompte_pct,
        remise_pct: workingDraft.remise_pct,
        tva_pct: tvaPct,
        acompte_jours: workingDraft.acompte_jours,
        mode_paiement: workingDraft.mode_paiement,
        nb_echeances: workingDraft.nb_echeances,
        statut: workingDraft.statut,
        version: workingDraft.version,
        parent_id: workingDraft.parent_id,
        structure: workingDraft.structure,
        echeancier_overrides: workingDraft.echeancier_overrides ?? null,
        texte_cloture: workingDraft.texte_cloture ?? null,
        conditions: remarques.trim() ? remarques : null,
        visuel_data_url: visuels[0]?.dataUrl ?? null,
        visuel_name: visuels[0]?.name ?? null,
        visuel2_data_url: visuels[1]?.dataUrl ?? null,
        visuel2_name: visuels[1]?.name ?? null,
        visuel3_data_url: visuels[2]?.dataUrl ?? null,
        visuel3_name: visuels[2]?.name ?? null,
      });

      let propId = workingDraft.id;
      if (!propId) {
        const { data: ins, error } = await supabase.from('propositions').insert(payload).select('id').single();
        if (error) throw error;
        propId = ins.id;
      } else {
        const { error } = await supabase.from('propositions').update(payload).eq('id', propId);
        if (error) throw error;
        await supabase.from('propositions_lignes').delete().eq('proposition_id', propId);
      }

      const lignesToSave = workingDraft.lignes.filter((l, i) => !excludedKeys.has(lineKey(l, i)));
      if (lignesToSave.length) {
        const lignesPayload = lignesToSave.map((l, i) => withDemoFlag({
          proposition_id: propId,
          ordre: i,
          titre: l.titre,
          description: l.description,
          quantite: l.quantite,
          prix_unitaire_ht: l.prixUnitaireHT,
          artiste_id: l.artisteId || null,
          reference: l.reference || null,
          unite: l.unite || null,
          remise_pct: l.remisePct || 0,
          prix_brut_ht: l.prixBrutHT ?? null,
          type_ligne: l.typeLigne || 'article',
        }));
        const { error: errL } = await supabase.from('propositions_lignes').insert(lignesPayload);
        if (errL) throw errL;
      }

      // Toute édition de la proposition réinitialise la notif de relance
      await resetRelanceNotificationForPrestation(workingDraft.prestation_id);

      toast.success(nextNumero ? `Proposition ${nextNumero} enregistrée` : 'Proposition enregistrée');
      onSaved();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const [factMenu, setFactMenu] = useState(false);

  /** Montant TTC déjà facturé (acomptes + intermédiaires) sur cette proposition. */
  const fetchDejaFacture = async (): Promise<number> => {
    const { data: fs } = await supabase.from('factures').select('id, type, statut, facture_lignes(quantite, prix_unitaire_ht, tva_pct)')
      .eq('proposition_id', draft.id).in('type', ['Acompte', 'Intermédiaire']).is('deleted_at', null).neq('statut', 'Annulée');
    return round2((fs || []).reduce((s, f: any) => s + (f.facture_lignes || []).reduce((t: number, l: any) => t + round2(Number(l.quantite) * Number(l.prix_unitaire_ht) * (1 + Number(l.tva_pct) / 100)), 0), 0));
  };

  const handleFacturePartielle = async (typeFact: 'Acompte' | 'Intermédiaire') => {
    if (!draft.id) return;
    const nom = typeFact === 'Acompte' ? "d'acompte" : 'intermédiaire';
    const deja = await fetchDejaFacture();
    const reste = resteAFacturer(totalTTC, [deja]);
    if (reste <= 0.005) { toast.error(`Tout le montant TTC (${formatEUR(totalTTC)}) est déjà facturé.`); return; }
    const defPct = typeFact === 'Acompte' ? Number(draft.acompte_pct || 30) : 30;
    const saisie = window.prompt(
      `Facture ${nom} — Total TTC : ${formatEUR(totalTTC)} · déjà facturé : ${formatEUR(deja)} · reste : ${formatEUR(reste)}\n\nSaisissez un pourcentage du total (ex : 30%) ou un montant TTC en euros (ex : 500) :`,
      `${defPct}%`,
    );
    if (saisie == null) return;
    const txt = saisie.trim().replace(/\s/g, '').replace(',', '.');
    const isPct = txt.endsWith('%');
    const n = parseFloat(txt.replace('%', '').replace('€', ''));
    if (!Number.isFinite(n) || n <= 0) { toast.error('Valeur invalide'); return; }
    const montant = round2(isPct ? totalTTC * n / 100 : n);
    if (montant > reste + 0.01) { toast.error(`Le montant dépasse le reste à facturer (${formatEUR(reste)})`); return; }
    if (!confirm(`Créer une facture ${nom} de ${formatEUR(montant)} TTC ?`)) return;
    setSaving(true);
    try {
      const libelle = `${typeFact === 'Acompte' ? 'Acompte' : 'Facture intermédiaire'}${isPct ? ` de ${String(n).replace('.', ',')} %` : ''} sur proposition ${draft.numero || ''}`.trim();
      const { error } = await (supabase.rpc as any)('creer_facture_partielle', { _proposition_id: draft.id, _montant_ttc: montant, _libelle: libelle, _type: typeFact });
      if (error) throw error;
      toast.success(`Facture ${nom} créée (${formatEUR(montant)} TTC) — visible dans Facturation`);
    } catch (e: any) {
      toast.error(e.message || 'Erreur de création');
    } finally {
      setSaving(false);
    }
  };

  const handleFinaliser = async () => {
    if (!draft.id) return;
    if (!confirm('Finaliser cette proposition ? Elle ne sera plus modifiable : toute correction créera une nouvelle version (l’originale est conservée).')) return;
    if (dirtyRef.current) { toast.error('Enregistrez d’abord vos modifications, puis finalisez.'); return; }
    const { error } = await supabase.from('propositions').update({ statut: 'Finalisée' as any }).eq('id', draft.id);
    if (error) { toast.error(error.message); return; }
    initialStatut.current = 'Finalisée';
    setDraft((d) => ({ ...d, statut: 'Finalisée' }));
    toast.success('Proposition finalisée');
  };

  const handleConvertToFacture = async () => {
    if (!draft.id) { toast.error('Enregistrez la proposition avant de la convertir'); return; }
    if (!confirm('Convertir cette proposition en facture ?')) return;
    setSaving(true);
    try {
      const { data, error } = await (supabase.rpc as any)('convertir_proposition_en_facture', { _proposition_id: draft.id });
      if (error) throw error;
      toast.success('Facture de solde / totale créée — visible dans Facturation');
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Erreur de conversion');
    } finally {
      setSaving(false);
    }
  };


  const buildEmailText = () => {
    const prenom = (draft.client_nom || '').split(' ')[0] || '';
    const lignesText = activeLignes
      .map((l) => {
        const head = `• ${l.titre || 'Prestation'} : ${l.quantite} × ${formatEUR(l.prixUnitaireHT)} = ${formatEUR(l.quantite * l.prixUnitaireHT)} HT`;
        return l.description?.trim() ? `${head}\n   ${l.description.trim()}` : head;
      })
      .join('\n');
    const note = (draft.note_intro || '').trim();
    const startsWithBonjour = /^bonjour\b/i.test(note);
    const intro = note
      ? (startsWithBonjour ? note : `Bonjour ${prenom},\n${note}`)
      : `Bonjour ${prenom},\nSuite à notre échange, veuillez trouver en pièce jointe notre proposition pour votre événement.`;
    return [
      intro,
      '',
      `Récapitulatif :`,
      `• Événement : ${draft.type_event || '—'}`,
      `• Date : ${draft.date_event ? formatDateFR(draft.date_event) : '—'}`,
      `• Lieu : ${draft.lieu || '—'}`,
      '',
      `Prestations :`,
      lignesText || '• —',
      ...(remarques.trim() ? ['', `Remarques :`, remarques.trim()] : []),
      '',
      `Total HT : ${formatEUR(totalHT)}`,
      `TVA (${String(tvaPct).replace('.', ',')}\u00A0%) : ${formatEUR(totalTVA)}`,
      `Total TTC : ${formatEUR(totalTTC)}`,
      `Acompte : ${draft.acompte_pct}% — ${draft.mode_paiement}`,
      
      '',
      (draft.texte_cloture ?? TEXTE_CLOTURE_DEFAUT),
      '',
      `— — —`,
      STRUCTURE_BRAND[draft.structure || 'cygnature'].name,
      `8 rue du Champ de la Gaule, 10120 Saint-Pouange`,
      `contact@cygnatureproduction.fr · 06\u00A071\u00A035\u00A015\u00A050`,
      `SIREN 898\u00A0473\u00A0962 · TVA FR60898473962 · Code APE 90.02Z`,
      `Licences spectacles : PLATESV-D-2022-008035 · PLATESV-D-2022-008036`,
      '',
      `PS : Plusieurs bons de commande sont en cours pour prestation de service, artiste ou matériels demandés à la même date. Seule la réception du bon de commande signé et sous réserve d'une confirmation de notre part au moment de la réception de ce dernier, vous permet de bloquer et de réserver l'artiste ou matériel si disponibles à la date de votre retour.`,
    ].join('\n');
  };

  const buildEmailSubject = () => {
    return `Proposition Cygnature — ${draft.type_event || 'votre événement'}${draft.date_event ? ` du ${formatDateFR(draft.date_event)}` : ''}`;
  };

  const fileName = () => {
    const slug = (draft.client_nom || 'client').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const date = draft.date_event ? draft.date_event.replace(/-/g, '') : new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `Proposition-Cygnature-${slug}-${date}.pdf`;
  };

  const clientRecord = useMemo(
    () => (draft.client_id ? data.clients.find((c) => c.id === draft.client_id) || null : null),
    [draft.client_id, data.clients],
  );

  const handleDownloadPdf = async () => {
    const html = renderPropositionHTML({ ...draft, lignes: activeLignes }, totalHT, echeancier, fileName(), { tvaPct, remise_pct: draft.remise_pct, remarques, calcByArtist, visuels, client: clientRecord });
    const name = fileName();
    try {
      await exportHtmlToPdf(html, { fileName: name, rootId: '__prop_pdf_root' });
    } catch (e: any) {
      console.error(e);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const effectiveSubject = () => mailSubjectOverride ?? buildEmailSubject();
  const effectiveBody = () => mailBodyOverride ?? buildEmailText();

  const handleCopyEmail = async () => {
    const text = `Objet : ${effectiveSubject()}\n\n${effectiveBody()}`;
    const ok = await copyTextToClipboard(text);
    if (ok) toast.success('Mail copié — collez-le dans votre client mail et joignez le PDF');
    else toast.error('Impossible de copier — sélectionnez le texte de l\'aperçu');
  };

  const [envoiOpen, setEnvoiOpen] = useState(false);
  const [livePreview, setLivePreview] = useState(false);
  const [liveHtml, setLiveHtml] = useState('');
  const buildHtml = () => renderPropositionHTML({ ...draft, lignes: activeLignes }, totalHT, echeancier, fileName(), { tvaPct, remise_pct: draft.remise_pct, remarques, calcByArtist, visuels, client: clientRecord });
  useEffect(() => {
    if (!livePreview) return;
    const id = setTimeout(() => { try { setLiveHtml(buildHtml()); } catch { /* aperçu indisponible */ } }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePreview, draft, activeLignes, totalHT, echeancier, tvaPct, remarques, visuels, clientRecord]);

  const handlePreview = () => {
    const html = renderPropositionHTML({ ...draft, lignes: activeLignes }, totalHT, echeancier, fileName(), { tvaPct, remise_pct: draft.remise_pct, remarques, calcByArtist, visuels, client: clientRecord });
    if (!openHtmlInNewTab(html)) toast.error('Aperçu bloqué par le navigateur — autorisez les pop-ups');
  };

  return (
    <div className={livePreview ? 'xl:pr-[46vw]' : ''}>
      {livePreview && (
        <div className="hidden xl:block fixed right-4 top-20 bottom-4 w-[44vw] z-30 bg-white rounded-xl overflow-hidden border border-slate-700 shadow-2xl">
          <iframe title="Aperçu en direct" srcDoc={liveHtml} className="w-full h-full border-0" sandbox="" />
        </div>
      )}
      {envoiOpen && (
        <EnvoiDocumentModal
          html={buildHtml()}
          fileName={fileName()}
          libelle={`la proposition ${draft.numero || ''}`.trim()}
          defaultTo={(clientRecord as any)?.email || (draft as any).client_email || ''}
          defaultSujet={effectiveSubject()}
          defaultMessage={effectiveBody()}
          onClose={() => setEnvoiOpen(false)}
          onSent={async () => {
            if (draft.id && ['Brouillon', 'Finalisée'].includes(draft.statut)) {
              await supabase.from('propositions').update({ statut: 'Envoyée', envoye_at: new Date().toISOString() }).eq('id', draft.id);
              initialStatut.current = 'Envoyée';
              setDraft((d) => ({ ...d, statut: 'Envoyée', envoye_at: new Date().toISOString() }));
            }
          }}
        />
      )}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X size={18} strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="text-2xl font-heading text-slate-100 flex items-center gap-3">
              <FileText className="text-amber-400" size={22} strokeWidth={1.5} />
              {draft.id ? `Proposition${draft.numero ? ` ${draft.numero}` : ''}` : 'Nouvelle proposition'}
              {draft.version > 1 && <span className="text-sm text-slate-400">V{draft.version}</span>}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">Saisie en HT · TVA {String(tvaPct).replace(".", ",")} % appliquée au total</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setLivePreview((v) => !v)} className="hidden xl:inline-flex items-center gap-1 px-3 py-2 border border-slate-700 text-slate-300 hover:bg-slate-800 rounded-lg text-sm" title="Afficher l’aperçu du document à droite, mis à jour en direct">
            <PanelRight size={14} strokeWidth={1.5} /> {livePreview ? 'Masquer l’aperçu' : 'Aperçu en direct'}
          </button>
          <select
            value={draft.statut}
            onChange={(e) => update('statut', e.target.value as Statut)}
            className={`px-3 py-2 rounded-lg text-sm border ${PROP_STATUT_COLORS[draft.statut]}`}
          >
            {STATUTS.map((s) => <option key={s} value={s} className="bg-slate-900 text-slate-200">{statutLabel(s)}</option>)}
          </select>
          {draft.id && draft.statut === 'Brouillon' && (
            <button
              onClick={handleFinaliser}
              disabled={saving}
              title="Figer la proposition : elle ne sera plus modifiable (dupliquer ou nouvelle version pour changer)"
              className="px-3 py-2 border border-indigo-500 text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-50 rounded-lg text-sm font-medium"
            >
              Finaliser
            </button>
          )}
          {draft.id && (
            <div className="relative">
              <button
                onClick={() => setFactMenu((v) => !v)}
                disabled={saving}
                className="px-3 py-2 border border-emerald-600 text-emerald-400 hover:bg-emerald-600/10 disabled:opacity-50 rounded-lg text-sm font-medium"
              >
                Créer une facture
              </button>
              {factMenu && (
                <div className="absolute right-0 z-40 mt-1 w-72 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-1 text-sm">
                  <button onClick={() => { setFactMenu(false); handleFacturePartielle('Acompte'); }} className="w-full text-left px-3 py-2 rounded hover:bg-slate-800 text-slate-200">Facture d'acompte <span className="block text-[11px] text-slate-500">% ou montant TTC</span></button>
                  <button onClick={() => { setFactMenu(false); handleFacturePartielle('Intermédiaire'); }} className="w-full text-left px-3 py-2 rounded hover:bg-slate-800 text-slate-200">Facture intermédiaire <span className="block text-[11px] text-slate-500">% ou montant TTC, en cours de prestation</span></button>
                  <button
                    onClick={() => { setFactMenu(false); handleConvertToFacture(); }}
                    disabled={draft.statut !== 'Acceptée'}
                    className="w-full text-left px-3 py-2 rounded hover:bg-slate-800 text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Facture de solde / totale
                    <span className="block text-[11px] text-slate-500">{draft.statut === 'Acceptée' ? 'Reprend toutes les lignes et déduit acomptes et intermédiaires déjà facturés' : 'Disponible une fois la proposition acceptée'}</span>
                  </button>
                </div>
              )}
            </div>
          )}
          <button id="prop-save-btn" onClick={handleSave} disabled={saving} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-950 rounded-lg text-sm font-medium">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {draft.id && ['Finalisée', 'Envoyée', 'Acceptée', 'Refusée', 'Annulée'].includes(initialStatut.current) && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-indigo-500/30 bg-indigo-500/10 text-sm text-indigo-200 flex items-center gap-2">
          <Lock size={14} strokeWidth={1.5} /> Proposition {statutLabel(initialStatut.current).toLowerCase()} : son contenu est figé. Toute modification enregistrée créera automatiquement une nouvelle version (l’originale reste intacte).
        </div>
      )}

      {/* Bloc Événement */}
      <Card className="p-6 mb-4">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-4 flex items-center justify-between">
          <span>Événement</span>
          {draft.prestation_id && (
            <span className="inline-flex items-center gap-2 text-emerald-400 normal-case tracking-normal">
              <Link2 size={12} strokeWidth={1.5} /> Lié à une prestation — infos synchronisées
              <Link
                to={`/crm-pro-evenementiel/nouvelle-demande?edit=${encodeURIComponent(draft.prestation_id)}`}
                className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              >
                <ExternalLink size={11} strokeWidth={1.5} /> Modifier la prestation
              </Link>
              <button
                onClick={() => handleLinkPrestation('')}
                className="ml-1 text-xs text-slate-500 hover:text-red-400"
              >Délier</button>
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <label className="block">
            <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Lier à une prestation</span>
            <select
              value={draft.prestation_id || ''}
              onChange={(e) => handleLinkPrestation(e.target.value)}
              className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
            >
              <option value="">— Aucune (calcul libre) —</option>
              {prestOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {(p.numero ? `${p.numero} · ` : '')}{p.client_nom || 'Sans client'}{p.date_event ? ` · ${formatDateFR(p.date_event)}` : ''}{p.lieu ? ` · ${p.lieu}` : ''}
                </option>
              ))}
            </select>
          </label>
          <Field label="Client *" value={draft.client_nom} onChange={(v) => update('client_nom', v)} placeholder="Nom du client" disabled={!!draft.prestation_id} />
          <Field label="Date événement" type="date" value={draft.date_event || ''} onChange={(v) => update('date_event', v || null)} disabled={!!draft.prestation_id} />
          <Field label="Lieu" value={draft.lieu || ''} onChange={(v) => update('lieu', v || null)} placeholder="Ville, domaine..." disabled={!!draft.prestation_id} />
          <Field label="Type d'événement" value={draft.type_event || ''} onChange={(v) => update('type_event', v || null)} placeholder="Mariage, Anniv..." disabled={!!draft.prestation_id} />
          <label className="block">
            <span className="text-[10px] text-slate-400 uppercase tracking-widest">Structure émettrice</span>
            <select
              value={draft.structure}
              onChange={(e) => update('structure', e.target.value as StructureCode)}
              className="mt-1 w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
            >
              <option value="cygnature">Cygnature Production</option>
              <option value="suivez_vos_reves">Suivez Vos Rêves Event</option>
            </select>
          </label>
        </div>
      </Card>

      {/* Bloc Lignes (lecture seule — reflet de la prestation liée) */}
      <Card className="p-6 mb-4">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Lignes de prestation (HT)</div>
            <div className="text-xs text-slate-500 mt-1">
              Reflet automatique de la prestation liée. Cochez/décochez un artiste pour l'inclure ou l'exclure de cette proposition (utile pour proposer un artiste seul ou un pack).
            </div>
          </div>
          <button
            onClick={() => { markDirty(); setDraft((d) => ({ ...d, lignes: [...d.lignes, { ordre: d.lignes.length, titre: '', description: '', quantite: 0, prixUnitaireHT: 0, artisteId: null, typeLigne: 'section' }] })); }}
            className="px-3 py-1.5 rounded-lg border border-slate-600 text-slate-300 text-xs hover:bg-slate-800"
            title="Ajouter un titre de section (non chiffré)"
          >+ Section</button>
          <button
            onClick={() => { markDirty(); setDraft((d) => ({ ...d, lignes: [...d.lignes, { ordre: d.lignes.length, titre: '', description: '', quantite: 1, prixUnitaireHT: 0, prixBrutHT: 0, artisteId: null, typeLigne: 'article' }] })); }}
            className="px-3 py-1.5 rounded-lg border border-slate-600 text-slate-300 text-xs hover:bg-slate-800"
            title="Ajouter une ligne libre"
          >+ Ligne libre</button>
          <CatalogueAdd onAdd={(l) => { markDirty(); setDraft((d) => ({ ...d, lignes: [...d.lignes, { ...l, ordre: d.lignes.length }] })); }} />
          {draft.lignes.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <button
                onClick={() => { markDirty(); setExcludedKeys(new Set()); }}
                className="px-2 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
              >Tout cocher</button>
              <button
                onClick={() => { markDirty(); setExcludedKeys(new Set(draft.lignes.map((l, i) => lineKey(l, i)))); }}
                className="px-2 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
              >Tout décocher</button>
              <span className="text-slate-500">{activeLignes.length}/{draft.lignes.length} actif(s)</span>
            </div>
          )}
        </div>

        {draft.lignes.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500">
            {draft.prestation_id
              ? 'Aucun artiste chiffré dans la prestation liée. Ouvrez « Modifier la prestation » pour ajouter le chiffrage.'
              : 'Liez une prestation pour afficher les lignes.'}
          </div>
        ) : (
          <div className="space-y-3">
            {draft.lignes.map((line, idx) => {
              const detail = line.artisteId ? calcByArtist[line.artisteId] : null;
              const c = detail?.calc as any;
              const optionRows: Array<{ label: string; note: string; tarif: number | null; marge: number | null; final: number }> = [];
              if (c) {
                [1, 2, 3, 4].forEach((n) => {
                  const on = c[`opt${n}On`];
                  const libre = Number(c[`opt${n}HT`]) || 0;
                  const tarif = Number(c[`opt${n}Tarif`]) || 0;
                  const marge = Number(c[`opt${n}Marge`]) || 0;
                  const final = libre > 0 ? libre : tarif * (1 + marge / 100);
                  const lib = String(c[`opt${n}Lib`] || '').trim();
                  const note = String(c[`opt${n}Note`] || '').trim();
                  if (on) optionRows.push({ label: lib || `Option ${n}`, note, tarif: tarif > 0 ? tarif : null, marge: marge > 0 ? marge : null, final });
                });
              }
              const lineTotal = line.quantite * line.prixUnitaireHT;
              const key = lineKey(line, idx);
              const excluded = excludedKeys.has(key);
              return (
                <div key={idx} className={`px-4 py-3 rounded-lg border text-sm transition-opacity ${excluded ? 'bg-slate-950/20 border-slate-800/40 opacity-50' : 'bg-slate-950/40 border-slate-800/60'}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <label className="flex items-start gap-2 flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!excluded}
                        onChange={() => toggleLine(key)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 text-amber-500 focus:ring-amber-500/40 cursor-pointer accent-amber-500"
                        title={excluded ? 'Inclure cet artiste dans la proposition' : 'Exclure cet artiste de cette proposition'}
                      />
                      {line.artisteId ? (
                        <span className={`font-medium ${excluded ? 'text-slate-500 line-through' : 'text-slate-200'}`}>{line.titre || 'Prestation'}</span>
                      ) : (
                        <input
                          value={line.titre}
                          onClick={(e) => e.preventDefault()}
                          onChange={(e) => updateLine(idx, { titre: e.target.value })}
                          placeholder={line.typeLigne === 'section' ? 'Titre de section' : 'Libellé du produit'}
                          className="flex-1 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 text-sm text-slate-200 placeholder:text-slate-600"
                        />
                      )}
                    </label>
                    {line.artisteId ? (
                      <div className="text-slate-300 tabular-nums text-xs whitespace-nowrap">× {line.quantite}</div>
                    ) : (
                      <div className="flex items-center gap-1.5 shrink-0">
                        {line.typeLigne !== 'section' && <input
                          value={String(line.quantite)}
                          onChange={(e) => updateLine(idx, { quantite: Number(e.target.value.replace(',', '.')) || 0 })}
                          inputMode="decimal"
                          title="Quantité"
                          className="w-14 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 text-right"
                        />}
                        {line.typeLigne !== 'section' && (<>
                        <input
                          value={line.unite || ''}
                          onChange={(e) => updateLine(idx, { unite: e.target.value })}
                          placeholder="Unité"
                          title="Unité"
                          className="w-16 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200"
                        />
                        <input
                          value={String(line.prixBrutHT ?? line.prixUnitaireHT)}
                          onChange={(e) => { const v = Number(e.target.value.replace(',', '.')) || 0; updateLine(idx, { prixBrutHT: v, prixUnitaireHT: prixNetHT(v, line.remisePct || 0) }); }}
                          inputMode="decimal"
                          title="Prix unitaire HT (avant remise)"
                          className="w-24 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 text-right"
                        />
                        <input
                          value={String(line.remisePct || 0)}
                          onChange={(e) => { const r = Number(e.target.value.replace(',', '.')) || 0; updateLine(idx, { remisePct: r, prixUnitaireHT: prixNetHT(line.prixBrutHT ?? line.prixUnitaireHT, r), prixBrutHT: line.prixBrutHT ?? line.prixUnitaireHT }); }}
                          inputMode="decimal"
                          title="Remise sur la ligne (%)"
                          className="w-14 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 text-right"
                        />
                        <span className="text-[10px] text-slate-500">%</span>
                        </>)}
                        <button
                          onClick={() => removeLine(idx)}
                          className="p-1 text-slate-500 hover:text-red-400"
                          title="Supprimer cette ligne"
                        >
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    )}
                  </div>
                  {optionRows.length > 0 ? (
                    <div className="space-y-1 mb-2 pl-2 border-l border-slate-800">
                      {optionRows.map((r, i) => (
                        <div key={i} className="text-xs text-slate-400">
                          <span>Comprenant {/^(option)\b/i.test(r.label) ? r.label : `Option ${r.label}`}{r.note ? ` — ${r.note}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  ) : line.artisteId ? (
                    line.description ? <div className="text-slate-400 text-xs mb-2">{line.description}</div> : null
                  ) : (
                    <textarea
                      value={line.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      rows={2}
                      placeholder="Description affichée au client…"
                      className="w-full mb-2 bg-slate-950/60 border border-slate-800 rounded px-2 py-1 text-xs text-slate-300 placeholder:text-slate-600 resize-y whitespace-pre-wrap break-words"
                    />
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t border-slate-800/60">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">Total ligne HT</span>
                    <span className="text-base text-slate-100 tabular-nums font-medium">{formatEUR(lineTotal)}</span>
                  </div>
                </div>
              );
            })}
            <div className="flex flex-wrap items-end justify-between gap-4 pt-3 border-t border-slate-800">
              <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">TVA</span>
                  <select
                    value={String(tvaPct)}
                    onChange={(e) => { markDirty(); setTvaPct(Number(e.target.value)); }}
                    className="px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200"
                  >
                    <option value="20">20 %</option>
                    <option value="10">10 %</option>
                    <option value="5.5">5,5 %</option>
                    <option value="0">0 %</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Remise (%)</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    value={String(draft.remise_pct)}
                    onChange={(e) => { markDirty(); update('remise_pct', Math.max(0, Math.min(100, Number(e.target.value) || 0))); }}
                    className="w-24 px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
                  />
                </label>
              </div>
              <div className="text-right space-y-1">
                <div className="flex justify-end gap-6 text-sm text-slate-300 tabular-nums">
                  <span className="text-slate-500">Total HT</span><span>{formatEUR(totalHT)}</span>
                </div>
                {draft.remise_pct > 0 && (
                  <div className="flex justify-end gap-6 text-sm text-slate-300 tabular-nums">
                    <span className="text-slate-500">Remise ({String(draft.remise_pct).replace('.', ',')}&nbsp;%)</span><span>-{formatEUR(remiseMontant)}</span>
                  </div>
                )}
                <div className="flex justify-end gap-6 text-sm text-slate-300 tabular-nums">
                  <span className="text-slate-500">Total HT après remise</span><span>{formatEUR(netHT)}</span>
                </div>
                <div className="flex justify-end gap-6 text-sm text-slate-300 tabular-nums">
                  <span className="text-slate-500">TVA ({String(tvaPct).replace('.', ',')}&nbsp;%)</span><span>{formatEUR(totalTVA)}</span>
                </div>
                <div className="flex justify-end gap-6 items-baseline">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">Total TTC</span>
                  <span className="text-2xl font-heading text-amber-400 tabular-nums">{formatEUR(totalTTC)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remarques / description complémentaire */}
        <div className="mt-5 pt-5 border-t border-slate-800">
          <label className="block">
            <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Remarques / description de la prestation</span>
            <textarea
              rows={4}
              value={remarques}
              onChange={(e) => { markDirty(); remarquesTouched.current = true; setRemarques(e.target.value); }}
              placeholder="Précisions techniques, conditions particulières, descriptif détaillé…"
              className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            />
          </label>
          <p className="text-xs text-slate-500 mt-1.5">Apparaîtra dans le PDF et le mail. Repris depuis la prestation liée si renseigné. Le texte coule sur toute la largeur (les retours simples deviennent des espaces, une ligne vide = nouveau paragraphe).</p>

          {/* Visuels optionnels (jusqu'à 3 images) joints à la proposition */}
          <div className="mt-4">
            <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Visuels (optionnel · max 3)</span>
            {visuels.map((v, i) => (
              <div key={i} className="flex items-center gap-3 flex-wrap mb-2">
                <label className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-200 cursor-pointer">
                  {v.dataUrl ? `Remplacer visuel ${i + 1}` : `Ajouter visuel ${i + 1}`}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 5 * 1024 * 1024) { alert('Image trop volumineuse (max 5 Mo).'); return; }
                      const reader = new FileReader();
                      reader.onload = () => {
                        markDirty();
                        setVisuels((prev) => {
                          const next = [...prev];
                          next[i] = { dataUrl: String(reader.result), name: f.name };
                          return next;
                        });
                      };
                      reader.readAsDataURL(f);
                    }}
                  />
                </label>
                {v.dataUrl && (
                  <>
                    <img src={v.dataUrl} alt={`Visuel ${i + 1}`} className="h-12 w-auto rounded border border-slate-700" />
                    <span className="text-xs text-slate-400 truncate max-w-[200px]">{v.name}</span>
                    <button
                      type="button"
                      onClick={() => { markDirty(); setVisuels((prev) => { const next = [...prev]; next[i] = { dataUrl: null, name: null }; return next; }); }}
                      className="text-xs text-rose-400 hover:text-rose-300"
                    >
                      Retirer
                    </button>
                  </>
                )}
              </div>
            ))}
            <p className="text-xs text-slate-500 mt-1.5">Images (PNG, JPG…) intégrées dans le PDF sous les remarques. Max 5 Mo chacune.</p>
          </div>
        </div>
      </Card>

      {/* Bloc Conditions */}
      <Card className="p-6 mb-4">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-4">Conditions de paiement</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          
          <Field label="Acompte %" value={String(draft.acompte_pct)} onChange={(v) => update('acompte_pct', Number(v) || 0)} />
          <Field label="Acompte sous (jours)" value={String(draft.acompte_jours)} onChange={(v) => update('acompte_jours', Math.max(0, Number(v) || 0))} />
          <Select label="Mode de paiement" value={draft.mode_paiement} onChange={(v) => update('mode_paiement', v)} options={data.paiementModes.map((m) => ({ value: m, label: m }))} />
          <label className="block">
            <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Nb échéances</span>
            <div className="flex items-center bg-slate-950/60 border border-slate-800 rounded-lg overflow-hidden focus-within:border-amber-500/50">
              <button
                type="button"
                onClick={() => update('nb_echeances', Math.max(1, (draft.nb_echeances || 1) - 1))}
                className="px-3 py-2 text-slate-300 hover:bg-slate-800/60 disabled:opacity-40"
                disabled={(draft.nb_echeances || 1) <= 1}
                aria-label="Diminuer le nombre d'échéances"
              >−</button>
              <input
                type="number"
                min={1}
                max={24}
                value={draft.nb_echeances}
                onChange={(e) => update('nb_echeances', Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-0 px-2 py-2 bg-transparent text-sm text-slate-200 text-center focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                type="button"
                onClick={() => update('nb_echeances', Math.min(24, (draft.nb_echeances || 1) + 1))}
                className="px-3 py-2 text-slate-300 hover:bg-slate-800/60 disabled:opacity-40"
                disabled={(draft.nb_echeances || 1) >= 24}
                aria-label="Augmenter le nombre d'échéances"
              >+</button>
            </div>
          </label>
        </div>

        {(echeancier.rows.length > 0 && totalHT > 0) && (
          <div className="mt-5 border-t border-slate-800 pt-4">
            <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-3 flex items-center justify-between">
              <span>Échéancier prévisionnel (TTC)</span>
              <span className="flex items-center gap-3">
                <span className="text-slate-500 normal-case tracking-normal">
                  Total TTC {formatEUR(totalTTC)} · Acompte {formatEUR(echeancier.acompte)} · Solde {formatEUR(echeancier.solde)}
                </span>
                {draft.echeancier_overrides && draft.echeancier_overrides.length > 0 && (
                  <button
                    type="button"
                    onClick={resetEcheancier}
                    className="text-[10px] text-amber-400 hover:text-amber-300 normal-case tracking-normal underline"
                  >
                    Réinitialiser
                  </button>
                )}
              </span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {echeancier.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-800/50 last:border-0">
                    <td className="py-2 text-slate-500 text-xs w-8 align-middle">{i + 1}</td>
                    <td className="py-1.5 align-middle">
                      <input
                        type="text"
                        value={r.label}
                        onChange={(e) => updateEcheance(i, { label: e.target.value })}
                        className="w-full px-2 py-1 bg-slate-950/60 border border-slate-800 rounded text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
                      />
                    </td>
                    <td className="py-1.5 align-middle w-44">
                      <input
                        type="date"
                        value={r.date}
                        onChange={(e) => {
                          e.currentTarget.dataset.invalid = e.currentTarget.validity.badInput ? '1' : '';
                          updateEcheance(i, { date: e.target.value });
                        }}
                        onBlur={(e) => {
                          if (e.currentTarget.validity.badInput) {
                            toast.error(`Date invalide pour « ${r.label} » (ex. 30/02 n'existe pas). Elle ne sera pas reprise dans la proposition.`);
                          }
                        }}
                        className="w-full px-2 py-1 bg-slate-950/60 border border-slate-800 rounded text-sm text-slate-200 focus:outline-none focus:border-amber-500/50 data-[invalid='1']:border-red-500 data-[invalid='1']:text-red-400"
                      />
                      {!r.date && <div className="text-[10px] text-red-400 mt-0.5">Date manquante ou invalide</div>}
                    </td>
                    <td className="py-1.5 align-middle w-36">
                      <div className="relative">
                        <input
                          type="number"
                          step="1"
                          value={r.montant}
                          onChange={(e) => updateEcheance(i, { montant: Number(e.target.value) || 0 })}
                          className="w-full pl-2 pr-6 py-1 bg-slate-950/60 border border-slate-800 rounded text-sm text-right text-amber-400 tabular-nums focus:outline-none focus:border-amber-500/50"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-amber-400 text-sm">€</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!echeancierEquilibre(echeancier.rows.map((r) => r.montant || 0), totalTTC) && (
              <div className="mt-2 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-red-500/40 bg-red-500/10 text-xs text-red-300">
                <span>Le total des échéances ({formatEUR(round2(echeancier.rows.reduce((s, r) => s + (r.montant || 0), 0)))}) ne correspond pas au total TTC ({formatEUR(totalTTC)}) — écart de {formatEUR(round2(totalTTC - echeancier.rows.reduce((s, r) => s + (r.montant || 0), 0)))}.</span>
                <button type="button" onClick={() => { const m = repartirSolde(echeancier.rows.map((r) => r.montant || 0), totalTTC); updateEcheance(m.length - 1, { montant: m[m.length - 1] }); }} className="shrink-0 px-2 py-1 rounded border border-red-400/50 hover:bg-red-500/20">Répartir le solde</button>
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-2">Libellés, dates et montants modifiables. Les valeurs personnalisées sont conservées avec la proposition.</p>
          </div>
        )}

        {/* Texte de clôture éditable */}
        <div className="mt-5 border-t border-slate-800 pt-4">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2 flex items-center justify-between">
            <span>Texte de clôture (mail &amp; PDF)</span>
            {draft.texte_cloture != null && (
              <button
                type="button"
                onClick={() => { markDirty(); setDraft((d) => ({ ...d, texte_cloture: null })); }}
                className="text-[10px] text-amber-400 hover:text-amber-300 normal-case tracking-normal underline"
              >
                Réinitialiser
              </button>
            )}
          </div>
          <textarea
            value={draft.texte_cloture ?? TEXTE_CLOTURE_DEFAUT}
            onChange={(e) => { markDirty(); setDraft((d) => ({ ...d, texte_cloture: e.target.value })); }}
            rows={6}
            className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50 font-mono leading-relaxed"
          />
        </div>
      </Card>


      {/* Bloc Envoi : PDF + copie mail */}
      <Card className="p-6 mb-4">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-4">Transmettre au client</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <button onClick={handlePreview} className="py-3 px-4 bg-slate-900/60 border border-slate-800 hover:bg-slate-800 rounded-lg text-sm text-slate-300 flex items-center justify-center gap-2">
            <Eye size={14} strokeWidth={1.5} /> Aperçu HTML
          </button>
          <button onClick={handleDownloadPdf} className="py-3 px-4 bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 text-amber-300 rounded-lg text-sm flex items-center justify-center gap-2">
            <Download size={14} strokeWidth={1.5} /> Télécharger le PDF
          </button>
          <button onClick={() => { if (!draft.id) { toast.error('Enregistrez la proposition avant de l’envoyer'); return; } setEnvoiOpen(true); }} className="py-3 px-4 bg-sky-500/15 border border-sky-500/40 hover:bg-sky-500/25 text-sky-300 rounded-lg text-sm flex items-center justify-center gap-2">
            <Send size={14} strokeWidth={1.5} /> Envoyer par e-mail
          </button>
          <button onClick={handleCopyEmail} className="py-3 px-4 bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 text-emerald-300 rounded-lg text-sm flex items-center justify-center gap-2">
            <ClipboardCopy size={14} strokeWidth={1.5} /> Copier le mail
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3 leading-relaxed">
          <b>Mode d'emploi :</b> 1) <i>Télécharger le PDF</i> (la fenêtre d'impression s'ouvre, choisissez « Enregistrer en PDF »). 2) <i>Copier le mail</i>, collez-le dans votre client (Gmail / Outlook), joignez le PDF téléchargé. Ou bien <i>Envoyer par e-mail</i> : l’envoi part directement, avec le PDF à télécharger.
        </p>
        <div className="mt-5 border border-slate-800 rounded-lg overflow-hidden">
          <div className="bg-slate-950/60 px-4 py-2 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 flex items-center justify-between gap-3">
            <span>Aperçu du mail{(mailSubjectOverride !== null || mailBodyOverride !== null) && <span className="ml-2 text-amber-400 normal-case tracking-normal">· modifié manuellement</span>}</span>
            <div className="flex items-center gap-2">
              {(mailSubjectOverride !== null || mailBodyOverride !== null) && (
                <button
                  onClick={() => { setMailSubjectOverride(null); setMailBodyOverride(null); toast.success('Mail régénéré depuis les données'); }}
                  className="text-[11px] px-2 py-1 rounded border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500 normal-case tracking-normal"
                  title="Régénérer le mail à partir des données de la proposition"
                >
                  <RefreshCcw size={11} strokeWidth={1.5} className="inline mr-1" /> Régénérer
                </button>
              )}
              <Dialog open={mailEditorOpen} onOpenChange={setMailEditorOpen}>
                <DialogTrigger asChild>
                  <button
                    onClick={() => {
                      setMailSubjectOverride((v) => v ?? buildEmailSubject());
                      setMailBodyOverride((v) => v ?? buildEmailText());
                    }}
                    className="text-[11px] px-2 py-1 rounded bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 normal-case tracking-normal flex items-center gap-1"
                  >
                    <Pencil size={11} strokeWidth={1.5} /> Éditer le mail
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-lg text-slate-100">Éditer le mail complet</DialogTitle>
                  </DialogHeader>
                  <div className="mt-2 space-y-3">
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Objet du mail</span>
                      <input
                        type="text"
                        value={mailSubjectOverride ?? buildEmailSubject()}
                        onChange={(e) => { markDirty(); setMailSubjectOverride(e.target.value); }}
                        className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">Corps du mail</span>
                      <textarea
                        rows={20}
                        value={mailBodyOverride ?? buildEmailText()}
                        onChange={(e) => { markDirty(); setMailBodyOverride(e.target.value); }}
                        className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50 resize-y font-mono leading-relaxed"
                      />
                    </label>
                    <p className="text-xs text-slate-500">Les modifications restent valables tant que vous ne cliquez pas sur Régénérer. Le PDF n'est pas affecté.</p>
                    <div className="flex justify-between items-center gap-2">
                      <button
                        onClick={() => { setMailSubjectOverride(buildEmailSubject()); setMailBodyOverride(buildEmailText()); toast.success('Mail réinitialisé'); }}
                        className="px-3 py-2 text-xs text-slate-400 hover:text-slate-200"
                      >
                        Réinitialiser depuis les données
                      </button>
                      <button
                        onClick={() => setMailEditorOpen(false)}
                        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-lg text-sm font-medium"
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="p-4 text-sm text-slate-300">
            <div className="text-xs text-slate-500 mb-2 flex items-start gap-2">
              <b className="shrink-0">Objet :</b>
              <input
                type="text"
                value={effectiveSubject()}
                onChange={(e) => { markDirty(); setMailSubjectOverride(e.target.value); }}
                className="flex-1 bg-transparent border-b border-dashed border-slate-700 focus:border-amber-500/60 outline-none text-slate-300 px-1 py-0.5"
                title="Cliquez pour modifier l'objet"
              />
            </div>
            <textarea
              value={effectiveBody()}
              onChange={(e) => { markDirty(); setMailBodyOverride(e.target.value); }}
              rows={Math.max(12, effectiveBody().split('\n').length + 1)}
              className="w-full whitespace-pre-wrap font-body text-slate-300 leading-relaxed bg-transparent border border-dashed border-slate-800 hover:border-slate-700 focus:border-amber-500/50 rounded p-2 outline-none resize-y"
              title="Cliquez pour modifier le corps du mail"
            />
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-amber-400">
              <FileText size={12} strokeWidth={1.5} /> Pièce jointe : {fileName()}
            </div>
          </div>
        </div>
      </Card>

      {/* Flèche flottante pour aller directement au bouton Enregistrer */}
      <button
        type="button"
        aria-label="Aller au bouton Enregistrer"
        title="Aller au bouton Enregistrer"
        onClick={() => {
          const el = document.getElementById('prop-save-btn');
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
        className="sticky bottom-4 float-right -mt-10 mr-1 z-10 w-10 h-10 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-lg flex items-center justify-center transition-colors"
      >
        <ChevronDown size={18} strokeWidth={2} />
      </button>
    </div>
  );
}

function Field(props: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; disabled?: boolean }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        placeholder={props.placeholder}
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
      />
    </label>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1.5 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50">
        {options.map((o) => <option key={`${label}-${o.value}`} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

/* ---------- HTML brandé proposition (impression / PDF) ---------- */
function escHtml(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

export type { Proposition, EcheanceOverride };
export function renderPropositionHTML(
  p: Proposition,
  totalHT: number,
  echeancier: { acompte: number; solde: number; rows: { label: string; date: string; montant: number }[] },
  fileName: string,
  extras: { tvaPct: number; remise_pct: number; remarques: string; calcByArtist?: Record<string, { calc: any; km: number | null; cout: number | null; prop_ttc: number | null; marge_pct: number | null }>; visuels?: Visual[]; client?: import('../types').Client | null }
): string {
  const lignes = p.lignes.map((l) => {
    if (l.typeLigne === 'section') return `<tr><td colspan="4" style="font-weight:bold;background:#f7f3ea;color:#121E36;text-transform:uppercase;letter-spacing:1px">${escHtml(l.titre || l.description)}</td></tr>`;
    if (l.typeLigne === 'commentaire') return `<tr><td colspan="4" style="font-style:italic;color:#5a6a7a;white-space:pre-wrap">${escHtml(l.titre || l.description)}</td></tr>`;
    if (l.typeLigne === 'saut_page') return `<tr style="page-break-after:always"><td colspan="4"></td></tr>`;
    const total = l.quantite * l.prixUnitaireHT;
    const titreHtml = (l.reference ? `<span style="font-family:monospace;color:#8a8a8a;font-size:11px">${escHtml(l.reference)}</span> ` : '') + (l.titre ? `<strong>${escHtml(l.titre)}</strong>` : '<strong>Prestation</strong>')
      + (l.remisePct ? `<div style="font-size:11px;color:#8a8a8a">Prix ${formatEUR(l.prixBrutHT ?? l.prixUnitaireHT)} − remise ${String(l.remisePct).replace('.', ',')} %</div>` : '');

    // Détail des options depuis le calculateur (si disponible)
    const detail = l.artisteId ? extras.calcByArtist?.[l.artisteId] : null;
    const c = detail?.calc as any;
    const optionRows: Array<{ label: string; note: string; tarif: number | null; marge: number | null; final: number }> = [];
    if (c) {
      [1, 2, 3, 4].forEach((n) => {
        const on = c[`opt${n}On`];
        const libre = Number(c[`opt${n}HT`]) || 0;
        const tarif = Number(c[`opt${n}Tarif`]) || 0;
        const marge = Number(c[`opt${n}Marge`]) || 0;
        const final = libre > 0 ? libre : tarif * (1 + marge / 100);
        const lib = String(c[`opt${n}Lib`] || '').trim();
        const note = String(c[`opt${n}Note`] || '').trim();
        if (on) optionRows.push({ label: lib || `Option ${n}`, note, tarif: tarif > 0 ? tarif : null, marge: marge > 0 ? marge : null, final });
      });
    }

    let detailHtml = '';
    if (optionRows.length > 0) {
      const itemsHtml = optionRows.map((r) => {
        const displayLabel = /^(option)\b/i.test(r.label) ? r.label : `Option ${r.label}`;
        const label = escHtml(displayLabel);
        const note = r.note ? ` — ${escHtml(r.note)}` : '';
        return `<div style="font-size:11px;color:#5a6a7a;padding:2px 0">Comprenant ${label}${note}</div>`;
      }).join('');

      detailHtml = `<div style="margin-top:6px;padding-left:10px;border-left:2px solid #ece4cf">${itemsHtml}</div>`;
    } else if (l.description?.trim()) {
      detailHtml = `<div style="font-size:11px;color:#5a6a7a;margin-top:3px;white-space:pre-wrap">${escHtml(l.description.trim())}</div>`;
    }

    return `<tr>
      <td>${titreHtml}${detailHtml}</td>
      <td class="r">${l.quantite}${l.unite ? ' ' + escHtml(l.unite) : ''}</td>
      <td class="r">${formatEUR(l.prixUnitaireHT)}</td>
      <td class="r"><strong>${formatEUR(total)}</strong></td>
    </tr>`;
  }).join('');

  const ech = echeancier.rows.length > 1
    ? `<table class="ech" data-pdf-section><thead><tr><th>Échéancier prévisionnel (TTC)</th><th class="r">Date</th><th class="r">Montant</th></tr></thead><tbody>${
        echeancier.rows.map((r) => `<tr><td>${escHtml(r.label)}</td><td class="r">${formatDateFR(r.date)}</td><td class="r"><strong>${formatEUR(r.montant)}</strong></td></tr>`).join('')
      }</tbody></table>` : '';

  const remisePct = Number(extras.remise_pct) || 0;
  const remiseMontant = remisePct > 0 ? Math.round(totalHT * (remisePct / 100) * 100) / 100 : 0;
  const netHT = totalHT - remiseMontant;
  const totalTVA = Math.round(netHT * (extras.tvaPct / 100) * 100) / 100;
  const totalTTC = Math.round((netHT + totalTVA) * 100) / 100;

  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${escHtml(fileName.replace(/\.pdf$/, ''))}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Source+Sans+3:wght@300;400;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 18mm 16mm; }
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Source Sans 3',Arial,sans-serif;color:#1c2937;background:#fdfbf5;font-size:12px;line-height:1.55;padding:0}
  .doc{max-width:780px;margin:0 auto;background:#fff;padding:32px 36px 40px}
  h1,h2,h3{font-family:'Libre Baskerville',Georgia,serif;font-weight:400;color:#0e2a3a}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #c8a96a;padding-bottom:18px;margin-bottom:24px}
  .brand .name{font-family:'Libre Baskerville',Georgia,serif;font-size:22px;color:#0e2a3a;letter-spacing:.5px}
  .brand .tag{font-size:10px;color:#7a8794;text-transform:uppercase;letter-spacing:2.5px;margin-top:4px}
  .doctitle{text-align:right}
  .doctitle h1{font-size:26px;color:#c8a96a;letter-spacing:1px}
  .doctitle .meta{font-size:11px;color:#7a8794;margin-top:6px}
  .badge{display:inline-block;padding:3px 10px;background:#fdf6e7;color:#a8862e;border:1px solid #e7d6a3;border-radius:3px;font-size:10px;font-weight:600;letter-spacing:.5px;margin-top:6px}
  .infos{display:grid;grid-template-columns:1fr 1fr;gap:18px;background:#faf6ec;border-left:3px solid #c8a96a;padding:16px 20px;margin-bottom:22px}
  .infos h3{font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#7a8794;font-family:'Source Sans 3',sans-serif;font-weight:600;margin-bottom:6px}
  .infos .v{font-size:13px;color:#0e2a3a}
  .intro{background:#fdfbf5;border:1px solid #ece4cf;padding:14px 18px;border-radius:4px;margin-bottom:22px;font-style:italic;color:#3a4a5a;white-space:pre-wrap}
  table{width:100%;border-collapse:collapse;margin-bottom:18px}
  th{background:#0e2a3a;color:#fff;text-align:left;padding:10px 12px;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;font-weight:600}
  td{padding:11px 12px;border-bottom:1px solid #ece4cf;vertical-align:top}
  td.r,th.r{text-align:right}
  .muted{color:#7a8794;font-size:11px}
  .totals{width:300px;margin-left:auto;margin-bottom:20px}
  .totals td{padding:8px 12px;border-bottom:1px solid #ece4cf}
  .totals .total td{background:#0e2a3a;color:#fff;font-weight:700;font-size:14px;border-bottom:none}
  .totals .acompte td{background:#fdf6e7;color:#a8862e;font-weight:600}
  .ech{margin-top:8px}
  .ech th{background:#3a4a5a}
  .conditions{background:#faf6ec;border:1px dashed #c8a96a;padding:14px 18px;border-radius:4px;font-size:11px;color:#3a4a5a;margin-bottom:18px;max-width:100%;overflow-wrap:anywhere;word-break:break-word}
  .conditions strong{color:#0e2a3a}
  .footer{border-top:1px solid #ece4cf;padding-top:14px;margin-top:24px;font-size:10px;color:#7a8794;text-align:center;line-height:1.6}
  .footer strong{color:#c8a96a}
  @media print{body{background:#fff}.doc{padding:0;max-width:none}}
</style></head><body>
<div class="doc">
  <div class="header" data-pdf-section>
    <div class="brand" style="display:flex;align-items:center;gap:14px">
      <img src="${logoSrc(p.structure)}" alt="${escHtml(STRUCTURE_BRAND[p.structure || 'cygnature'].alt)}" style="display:block;height:auto;width:64px;max-height:80px;object-fit:contain;flex:0 0 auto;padding:2px 0"/>
      <div>
        <div class="name">${STRUCTURE_BRAND[p.structure || 'cygnature'].name}</div>
        <div class="tag">${STRUCTURE_BRAND[p.structure || 'cygnature'].tagline}</div>
      </div>
    </div>
    <div class="doctitle">
      <h1>Proposition</h1>
      <div class="meta">${p.numero ? `N° ${escHtml(p.numero)}` : ''}</div>
      
      <div class="badge">Sous réserve de disponibilité au moment de votre accord</div>
    </div>
  </div>

  ${(() => {
    const cli = extras.client;
    const titre = cli?.societe ? `${escHtml(cli.societe)}` : '';
    const contact = cli ? `${escHtml([cli.prenom, cli.nom].filter(Boolean).join(' '))}` : escHtml(p.client_nom || '—');
    const fonction = cli?.fonction ? `<div class="muted" style="margin-top:2px">${escHtml(cli.fonction)}</div>` : '';
    const [adrRue, adrCpVille] = adresseLignes(cli?.adresse, cli?.cp, cli?.ville);
    const adrLigne1 = adrRue ? `<div style="margin-top:6px">${escHtml(adrRue)}</div>` : '';
    const adrLigne2 = adrCpVille ? `<div>${escHtml(adrCpVille)}</div>` : '';
    const adrLigne3 = cli?.pays && cli.pays !== 'France' ? `<div>${escHtml(cli.pays)}</div>` : '';
    const tel = cli?.tel ? `<div style="margin-top:6px">Tél : ${escHtml(cli.tel)}</div>` : '';
    const email = cli?.email ? `<div>Email : ${escHtml(cli.email)}</div>` : '';
    const siret = cli?.siret ? `<div class="muted" style="margin-top:4px">SIRET : ${escHtml(cli.siret)}</div>` : '';
    const head = titre
      ? `<div class="v"><strong>${titre}</strong></div><div class="v" style="margin-top:2px">${contact}</div>`
      : `<div class="v"><strong>${contact}</strong></div>`;
    return `<div class="infos" data-pdf-section>
      <div>
        <h3>Destinataire</h3>
        ${head}
        ${fonction}
        ${adrLigne1}${adrLigne2}${adrLigne3}
        ${tel}${email}
        ${siret}
      </div>
      <div style="text-align:right">
        <h3>Événement</h3>
        <div class="v">${escHtml(p.type_event || '—')}</div>
        <div class="muted" style="margin-top:4px">${p.date_event ? formatDateFR(p.date_event) : '—'}${p.lieu ? ` · ${escHtml(p.lieu)}` : ''}</div>
      </div>
    </div>`;
  })()}

  ${p.note_intro ? `<div class="intro" data-pdf-section>${escHtml(p.note_intro)}</div>` : ''}

  <table data-pdf-section>
    <thead><tr><th>Prestation</th><th class="r">Qté</th><th class="r">P.U. HT</th><th class="r">Total HT</th></tr></thead>
    <tbody>${lignes || '<tr><td colspan="4" style="text-align:center;color:#7a8794;padding:20px">Aucune ligne renseignée</td></tr>'}</tbody>
  </table>

  <table class="totals" data-pdf-section>
    <tr><td>Total HT</td><td class="r">${formatEUR(totalHT)}</td></tr>
    ${remisePct > 0 ? `<tr><td>Remise (${String(remisePct).replace('.', ',')}\u00A0%)</td><td class="r">-${formatEUR(remiseMontant)}</td></tr>` : ''}
    <tr><td>TVA (${String(extras.tvaPct).replace('.', ',')}\u00A0%)</td><td class="r">${formatEUR(totalTVA)}</td></tr>
    <tr class="total"><td>TOTAL TTC</td><td class="r">${formatEUR(totalTTC)}</td></tr>
    ${p.acompte_pct ? `<tr class="acompte"><td>Acompte (${p.acompte_pct}%) TTC</td><td class="r">${formatEUR(echeancier.acompte)}</td></tr>` : ''}
    ${p.acompte_pct ? `<tr><td>Solde TTC</td><td class="r">${formatEUR(echeancier.solde)}</td></tr>` : ''}
  </table>

  ${(() => {
    const txt = extras.remarques?.trim();
    const visuals = (extras.visuels || []).filter((v) => v.dataUrl);
    if (!txt && !visuals.length) return '';
    // Convertit le texte brut en paragraphes : ligne vide = nouveau paragraphe ;
    // simples retours à la ligne = espace (texte qui coule sur toute la largeur).
    const paragraphs = (txt || '')
      .split(/\n\s*\n+/)
      .map((p) => escHtml(p.replace(/\s*\n\s*/g, ' ').trim()))
      .filter(Boolean);
    const cols = visuals.length > 2 ? 3 : visuals.length;
    const visuelsHtml = visuals.length
      ? `<div style="margin-top:12px;display:grid;grid-template-columns:repeat(${cols}, 1fr);gap:12px">${visuals.map((v, i) => `<img src="${v.dataUrl}" alt="Visuel ${i + 1}" style="width:100%;max-height:380px;border-radius:4px;border:1px solid #ece4cf;object-fit:cover"/>`).join('')}</div>`
      : '';
    const corps = paragraphs.length
      ? `<div style="margin-top:6px;max-width:100%;overflow-wrap:anywhere;word-break:break-word">${paragraphs.map((p) => `<p style="margin:0 0 8px 0;text-align:justify;overflow-wrap:anywhere;word-break:break-word">${p}</p>`).join('')}</div>`
      : '';
    return `<div class="conditions" data-pdf-section><strong>Remarques :</strong>${corps}${visuelsHtml}</div>`;
  })()}

  ${ech}

  <div class="conditions" data-pdf-section>
    <strong>Conditions :</strong> Proposition en HT (TVA applicable selon régime fiscal du client).
    Mode de paiement : <strong>${escHtml(p.mode_paiement)}</strong>${p.acompte_pct ? ` — Acompte de ${p.acompte_pct}% à la confirmation, solde ${(p.nb_echeances || 1) > 1 ? `en ${p.nb_echeances} échéances` : 'le jour de la prestation'}.` : '.'}
    Cette proposition n'a pas valeur de devis contractuel — un devis officiel sera émis à votre validation.
  </div>

  <div class="conditions" data-pdf-section style="background:#fff;border-style:solid;border-color:#ece4cf;white-space:pre-wrap">${escHtml((p.texte_cloture ?? TEXTE_CLOTURE_DEFAUT))}</div>


  <div class="conditions" data-pdf-section style="font-size:10px;color:#7a8794">
    <strong>PS :</strong> Plusieurs bons de commande sont en cours pour prestation de service, artiste ou matériels demandés à la même date. Seule la réception du bon de commande signé et sous réserve d'une confirmation de notre part au moment de la réception de ce dernier, vous permet de bloquer et de réserver l'artiste ou matériel si disponibles à la date de votre retour.
  </div>

  <div class="footer" data-pdf-section>
    <strong>${STRUCTURE_BRAND[p.structure || 'cygnature'].name}</strong><br/>
    8 rue du Champ de la Gaule · 10120 Saint-Pouange · contact@cygnatureproduction.fr · 06\u00A071\u00A035\u00A015\u00A050<br/>
    SASU au capital de 150,00\u00A0€ · SIREN 898\u00A0473\u00A0962 · SIRET 898\u00A0473\u00A0962\u00A000014 · TVA FR60898473962 · RCS Troyes · Code APE 90.02Z<br/>
    Licences spectacles : PLATESV-D-2022-008035 (cat. 2) · PLATESV-D-2022-008036 (cat. 3)<br/>
    Document généré le ${formatDateFR(new Date().toISOString().slice(0, 10))}
  </div>
</div>
<script>window.onafterprint=function(){};</script>
</body></html>`;
}
